

















import { useEffect, useRef, useState, useCallback } from "react";
import { useCallStore } from "../app/callStore";
import { getSocket } from "../socket/socket";
import { Phone, Video, Mic, MicOff, VideoOff, PhoneOff, Minimize2, Maximize2, Users, Volume2, VolumeX, ShieldCheck } from "lucide-react";
import ringtoneSound from "../assets/VibrateCall.mp3";

export default function CallManager() {
  const {
    isReceivingCall,
    isCalling,
    callActive,
    callerName,
    callerSignal,
    callerId,
    targetUserId,
    targetUserName,
    groupId,
    groupName,
    participants,
    isVideo,
    setCallState,
    clearCallState,
  } = useCallStore();

  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null); // For 1-on-1
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [isBusyResponse, setIsBusyResponse] = useState(false);
  const [showCallEndedModal, setShowCallEndedModal] = useState(false);
  const [callDuration, setCallDuration] = useState(0);

  const durationIntervalRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const connectionRef = useRef(null); // For 1-on-1
  const peersRef = useRef({}); // For Group: { userId: RTCPeerConnection }
  
  const ringtoneRef = useRef(null);
  const vibrateInterval = useRef(null);
  const callInitiatedRef = useRef(false);

  // ==========================================
  // 🎵 HELPERS
  // ==========================================

  const startRinging = useCallback(() => {
    if (!ringtoneRef.current) {
      ringtoneRef.current = new Audio(ringtoneSound);
      ringtoneRef.current.loop = true;
    }
    ringtoneRef.current.play().then(() => {
      setAutoplayBlocked(false);
    }).catch(e => {
      console.log("Audio block:", e);
      setAutoplayBlocked(true);
    });
    if (navigator.vibrate) {
      vibrateInterval.current = setInterval(() => {
        navigator.vibrate([400, 200, 400]);
      }, 1200);
    }
  }, []);

  const stopRinging = useCallback(() => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
    if (vibrateInterval.current) clearInterval(vibrateInterval.current);
    if (navigator.vibrate) navigator.vibrate(0);
  }, []);

  const initStream = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      stream.getVideoTracks().forEach(track => {
        track.enabled = true;
      });

      setLocalStream(stream);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      return stream;
    } catch (err) {
      console.error("Stream access denied:", err);
      return null;
    }
  }, []);

  const createPeer = useCallback((stream, socket, toUserId) => {
    const peer = new RTCPeerConnection({
      iceServers: [{ urls: "stun:stun.l.google.com:19302" }]
    });
  
    stream.getTracks().forEach(track => {
      peer.addTrack(track, stream);
    });
  
    peer.ontrack = (event) => {
      console.log("🎬 [1-on-1] Remote track received:", event.track.kind, event.streams);
      if (event.streams && event.streams[0]) {
        event.streams[0].getTracks().forEach(track => {
          track.enabled = true;
          console.log(`📡 Track ${track.kind} enabled`);
        });
        setRemoteStream(event.streams[0]);
      }
    };

    peer.onicecandidate = (event) => {
      if (event.candidate) {
        console.log("❄️ [1-on-1] ICE Candidate generated");
        socket.emit("ice_candidate", {
          to: toUserId,
          candidate: event.candidate
        });
      }
    };
  
    return peer;
  }, []);

  const endCallCleanup = useCallback((showModal = false) => {
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
    }
    if (remoteStream) {
      remoteStream.getTracks().forEach(track => track.stop());
    }
    if (connectionRef.current) {
      connectionRef.current.close();
      connectionRef.current = null;
    }
    
    // Cleanup Group Peers
    Object.values(peersRef.current).forEach(peer => peer.close());
    peersRef.current = {};

    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }

    setLocalStream(null);
    setRemoteStream(null);
    setCallDuration(0);
    clearCallState();
    stopRinging();
    callInitiatedRef.current = false;
    
    if (showModal) {
      setShowCallEndedModal(true);
      setTimeout(() => setShowCallEndedModal(false), 3000);
    }
  }, [localStream, remoteStream, clearCallState, stopRinging]);

  // ==========================================
  // 📡 WEBRTC GROUP MESH HELPERS
  // ==========================================

  const createGroupPeer = useCallback((targetUserId, stream, socket) => {
    const peer = new RTCPeerConnection({
      iceServers: [{ urls: "stun:stun.l.google.com:19302" }]
    });

    stream.getTracks().forEach(track => peer.addTrack(track, stream));

    peer.ontrack = (event) => {
      console.log(`🎬 Received track from ${targetUserId}`);
      setCallState({
        participants: useCallStore.getState().participants.map(p => 
          p.userId === targetUserId ? { ...p, stream: event.streams[0] } : p
        )
      });
    };

    peer.onicecandidate = (event) => {
      if (event.candidate) {
        socket.emit("webrtc_ice_candidate", {
          targetUserId,
          candidate: event.candidate
        });
      }
    };

    return peer;
  }, [setCallState]);

  // ==========================================
  // 🧨 EFFECTS
  // ==========================================

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const handleIncomingCall = (data) => {
      if (isReceivingCall || isCalling || callActive) {
        socket.emit("call_busy", { to: data.from });
        return;
      }
      setCallState({
        isReceivingCall: true,
        callerId: data.from,
        callerName: data.name,
        callerSignal: data.signal,
        isVideo: data.isVideo,
      });
      startRinging();
    };

    const handleIncomingGroupCall = (data) => {
      if (isReceivingCall || isCalling || callActive) {
        // socket.emit("call_busy", { to: data.from });
        return;
      }
      setCallState({
        isReceivingCall: true,
        groupId: data.groupId,
        groupName: data.groupName,
        callerId: data.from,
        callerName: data.senderName,
        isVideo: data.isVideo
      });
      startRinging();
    };

    const handleGroupCallStarted = (data) => {
      const activeCalls = useCallStore.getState().activeGroupCalls;
      if (!activeCalls.find(c => c.groupId === data.groupId)) {
        setCallState({
          activeGroupCalls: [...activeCalls, data]
        });
      }
      
      // Also trigger incoming call modal if we are a member
      handleIncomingGroupCall(data);
    };

    const handleGroupCallEnded = ({ groupId }) => {
      setCallState({
        activeGroupCalls: useCallStore.getState().activeGroupCalls.filter(c => c.groupId !== groupId)
      });
    };

    const handleActiveGroupCalls = (calls) => {
      console.log("📡 Initial active group calls:", calls);
      setCallState({ activeGroupCalls: calls });
    };

    const handleUserBusy = () => {
      setIsBusyResponse(true);
      setTimeout(() => setIsBusyResponse(false), 5000);
      endCallCleanup();
    };

    const handleCallAccepted = async (signal) => {
      console.log("✅ [1-on-1] Call Accepted");
      stopRinging();
      setCallState({ callActive: true, isCalling: false });
      if (connectionRef.current) {
        try {
          await connectionRef.current.setRemoteDescription(new RTCSessionDescription(signal));
          // Process queued ICE candidates
          while (iceCandidateQueue.current.length > 0) {
            const candidate = iceCandidateQueue.current.shift();
            await connectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
          }
        } catch (e) {
          console.error("Error setting remote description:", e);
        }
      }
    };

    const handleCallRejected = () => {
      stopRinging();
      endCallCleanup();
    };

    const handleCallEnded = () => {
      console.log("☎️ Received call_ended from socket");
      stopRinging();
      endCallCleanup(true);
    };

    const handleIceCandidate = async (candidate) => {
      if (connectionRef.current && candidate) {
        if (connectionRef.current.remoteDescription) {
          try {
            await connectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
          } catch (e) {
            console.error("Error adding ice candidate:", e);
          }
        } else {
          iceCandidateQueue.current.push(candidate);
        }
      }
    };

    // --- GROUP CALL EVENTS ---
    const handleUserJoinedCall = async ({ userId, name }) => {
      console.log(`👤 User joined call: ${userId} (${name})`);
      const currentParticipants = useCallStore.getState().participants;
      if (!currentParticipants.find(p => p.userId === userId)) {
        setCallState({
          participants: [...currentParticipants, { userId, stream: null, name: name || `User ${userId}` }]
        });
      }
    };

    const handleUserLeftCall = ({ userId, name }) => {
      if (peersRef.current[userId]) {
        peersRef.current[userId].close();
        delete peersRef.current[userId];
      }
      setCallState({
        participants: useCallStore.getState().participants.filter(p => p.userId !== userId)
      });
      // Optionally show a notification
    };

    const handleExistingParticipants = ({ users }) => {
      console.log("👥 Existing participants:", users);
      setCallState({
        participants: users.map(u => ({ userId: u.userId, stream: null, name: u.name }))
      });
    };

    const handleWebRTCOffer = async ({ from, signal }) => {
      const stream = localStream || await initStream();
      const peer = createGroupPeer(from, stream, socket);
      peersRef.current[from] = peer;
      await peer.setRemoteDescription(new RTCSessionDescription(signal));
      const answer = await peer.createAnswer();
      await peer.setLocalDescription(answer);
      socket.emit("webrtc_answer", { targetUserId: from, signal: answer });
    };

    const handleWebRTCAnswer = async ({ from, signal }) => {
      const peer = peersRef.current[from];
      if (peer) {
        await peer.setRemoteDescription(new RTCSessionDescription(signal));
      }
    };

    const handleWebRTCIce = async ({ from, candidate }) => {
      const peer = peersRef.current[from];
      if (peer && candidate) {
        await peer.addIceCandidate(new RTCIceCandidate(candidate));
      }
    };

    socket.on("incoming_call", handleIncomingCall);
    socket.on("incoming_group_call", handleIncomingGroupCall);
    socket.on("group_call_started", handleGroupCallStarted);
    socket.on("group_call_ended", handleGroupCallEnded);
    socket.on("active_group_calls", handleActiveGroupCalls);
    socket.on("user_busy", handleUserBusy);
    socket.on("call_accepted", handleCallAccepted);
    socket.on("call_rejected", handleCallRejected);
    socket.on("call_ended", handleCallEnded);
    socket.on("ice_candidate", handleIceCandidate);

    socket.on("user_joined_call", handleUserJoinedCall);
    socket.on("user_left_call", handleUserLeftCall);
    socket.on("existing_participants", handleExistingParticipants);
    socket.on("webrtc_offer", handleWebRTCOffer);
    socket.on("webrtc_answer", handleWebRTCAnswer);
    socket.on("webrtc_ice_candidate", handleWebRTCIce);

    return () => {
      socket.off("incoming_call", handleIncomingCall);
      socket.off("incoming_group_call", handleIncomingGroupCall);
      socket.off("group_call_started", handleGroupCallStarted);
      socket.off("group_call_ended", handleGroupCallEnded);
      socket.off("active_group_calls", handleActiveGroupCalls);
      socket.off("user_busy", handleUserBusy);
      socket.off("call_accepted", handleCallAccepted);
      socket.off("call_rejected", handleCallRejected);
      socket.off("call_ended", handleCallEnded);
      socket.off("ice_candidate", handleIceCandidate);

      socket.off("user_joined_call", handleUserJoinedCall);
      socket.off("user_left_call", handleUserLeftCall);
      socket.off("existing_participants", handleExistingParticipants);
      socket.off("webrtc_offer", handleWebRTCOffer);
      socket.off("webrtc_answer", handleWebRTCAnswer);
      socket.off("webrtc_ice_candidate", handleWebRTCIce);
    };
  }, [isReceivingCall, isCalling, callActive, localStream, groupId, setCallState, startRinging, endCallCleanup, createGroupPeer, initStream]);

  useEffect(() => {
    if (callActive && !localStream) {
      initStream();
    }
  }, [callActive, localStream, initStream]);

  useEffect(() => {
    if (isCalling && !callActive && !callInitiatedRef.current) {
      callInitiatedRef.current = true;
      initiateCallFlow();
    }
  }, [isCalling, callActive]);

  useEffect(() => {
    if (!callActive || !localStream || !groupId) return;
    
    const socket = getSocket();
    if (!socket) return;
    const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
    
    participants.forEach(async (p) => {
      // If we don't have a peer for this participant yet AND we are the lower ID (initiator)
      if (!peersRef.current[p.userId] && Number(currentUser.id) < Number(p.userId)) {
         console.log(`📡 Auto-negotiating: Initiating offer to ${p.userId}`);
         const peer = createGroupPeer(p.userId, localStream, socket);
         peersRef.current[p.userId] = peer;
         try {
           const offer = await peer.createOffer();
           await peer.setLocalDescription(offer);
           socket.emit("webrtc_offer", { targetUserId: p.userId, signal: offer, groupId });
         } catch (e) {
           console.error("Negotiation error:", e);
         }
      }
    });
  }, [callActive, localStream, participants, groupId, createGroupPeer]);

  const iceCandidateQueue = useRef([]);

  useEffect(() => {
    if (localVideoRef.current && localStream && localVideoRef.current.srcObject !== localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream, callActive]);

  useEffect(() => {
    if (callActive && !durationIntervalRef.current) {
      durationIntervalRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (!callActive && durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }
    };
  }, [callActive]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const initiateCallFlow = async () => {
    const socket = getSocket();
    if (!socket) return;
    try {
      const stream = await initStream();
      if (!stream) {
        clearCallState();
        return;
      }

      if (groupId) {
        console.log("📞 Initiating Group Call:", groupId);
        socket.emit("start_group_call", { groupId, isVideo });
        socket.emit("join_group_call", { groupId });
        setCallState({ callActive: true, isCalling: false });
      } else {
        console.log("📞 Initiating 1-on-1 Call to:", targetUserId);
        const peer = createPeer(stream, socket, targetUserId);
        connectionRef.current = peer;
        const offer = await peer.createOffer();
        await peer.setLocalDescription(offer);
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        socket.emit("call_user", {
          userToCall: targetUserId,
          signalData: offer,
          from: user.id,
          name: `${user.first_name} ${user.last_name || ""}`.trim(),
          isVideo: isVideo,
        });
      }
    } catch (err) {
      console.error("Initiate error:", err);
      endCallCleanup();
    }
  };

  // ==========================================
  // 🎮 ACTIONS
  // ==========================================

  const answerCall = async () => {
    stopRinging();
    const stream = await initStream();
    if (!stream) return;
    
    const socket = getSocket();
    if (groupId) {
      setCallState({ callActive: true, isReceivingCall: false });
      socket.emit("join_group_call", { groupId });
    } else {
      setCallState({ callActive: true, isReceivingCall: false });
      const peer = createPeer(stream, socket, callerId);
      connectionRef.current = peer;
      try {
        await peer.setRemoteDescription(new RTCSessionDescription(callerSignal));
        const answer = await peer.createAnswer();
        await peer.setLocalDescription(answer);
        socket.emit("make_answer", { to: callerId, signal: answer });
      } catch (e) {
        console.error("Answer error:", e);
        endCallCleanup();
      }
    }
  };

  const rejectCall = () => {
    stopRinging();
    const socket = getSocket();
    if (socket) socket.emit("reject_call", { to: callerId });
    clearCallState();
  };

  const endCall = () => {
    const socket = getSocket();
    const target = callerId || targetUserId;
    console.log("☎️ Ending call, notifying:", target);
    if (socket) {
      if (groupId) socket.emit("leave_group_call", { groupId });
      else if (target) socket.emit("end_call", { to: target });
    }
    endCallCleanup(true); // Always show modal when we end the call locally
  };

  const toggleMute = () => {
    if (localStream) {
      localStream.getAudioTracks().forEach(track => {
        track.enabled = !track.enabled;
        setIsMuted(!track.enabled);
      });
    }
  };

  const toggleCamera = () => {
    if (localStream) {
      localStream.getVideoTracks().forEach(track => {
        track.enabled = !track.enabled;
        setIsCameraOff(!track.enabled);
      });
    }
  };

  const toggleSpeaker = () => {
    setIsSpeakerOn(!isSpeakerOn);
    // In a browser, speaker toggling is usually handled by the system, 
    // but we can simulate it for UI purposes.
  };

  if (!isCalling && !isReceivingCall && !callActive && !isBusyResponse) return null;

  return (
    <div className={`fixed inset-0 z-[2000000] transition-all duration-700 flex items-center justify-center ${isMinimized ? 'pointer-events-none' : 'bg-[#0b1014] text-white'}`}>
      
      {/* ⚠️ --- BUSY RESPONSE MODAL --- */}
      {isBusyResponse && (
        <div className="fixed inset-0 z-[2000001] bg-black/60 flex items-center justify-center p-6 backdrop-blur-sm">
          <div className="bg-[#232d36] rounded-3xl p-8 max-w-sm w-full shadow-2xl border border-white/10 animate-in zoom-in-95">
             <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <PhoneOff className="w-10 h-10 text-red-500" />
             </div>
             <h3 className="text-2xl font-bold text-center mb-2">User Busy</h3>
             <p className="text-gray-400 text-center mb-8">The person you are trying to call is currently on another call.</p>
             <button onClick={() => setIsBusyResponse(false)} className="w-full py-4 bg-[#00a884] hover:bg-[#008f72] rounded-full font-bold transition-colors">
               Dismiss
             </button>
          </div>
        </div>
      )}

      {/* 📞 --- CALL ENDED MODAL --- */}
      {showCallEndedModal && (
        <div className="fixed inset-0 z-[2000001] bg-black/60 flex items-center justify-center p-6 backdrop-blur-sm">
          <div className="bg-[#232d36] rounded-3xl p-8 max-w-sm w-full shadow-2xl border border-white/10 animate-in zoom-in-95">
             <div className="w-20 h-20 bg-gray-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <PhoneOff className="w-10 h-10 text-gray-400" />
             </div>
             <h3 className="text-2xl font-bold text-center mb-2">Call Ended</h3>
             <p className="text-gray-400 text-center mb-8">The call has ended.</p>
             <button onClick={() => setShowCallEndedModal(false)} className="w-full py-4 bg-gray-600 hover:bg-gray-700 rounded-full font-bold transition-colors">
               Dismiss
             </button>
          </div>
        </div>
      )}

      {/* 🔮 --- INCOMING CALL MODAL (WhatsApp Style) --- */}
      {isReceivingCall && !callActive && (
        <div className="w-full h-full bg-[#0b1014] flex flex-col items-center justify-between py-20 px-10 animate-in fade-in duration-500">
           <div className="flex flex-col items-center gap-4">
              <div className="w-32 h-32 bg-[#232d36] rounded-full flex items-center justify-center overflow-hidden border-4 border-[#00a884]">
                 {groupId ? <Users className="w-16 h-16 text-white" /> : <div className="text-6xl font-bold">{(callerName?.charAt(0).toUpperCase() || "U")}</div>}
              </div>
              <h2 className="text-4xl font-semibold">{groupId ? groupName : callerName}</h2>
              <p className="text-[#00a884] font-medium tracking-wide uppercase text-sm">WhatsApp Call</p>
           </div>
           
           <div className="flex flex-col items-center gap-10">
              {autoplayBlocked && (
                <button onClick={() => { ringtoneRef.current?.play(); setAutoplayBlocked(false); }} className="text-[#00a884] text-xs underline animate-pulse">
                   Click to enable ringtone
                </button>
              )}
              <div className="flex gap-20">
                <button onClick={rejectCall} className="group flex flex-col items-center gap-3">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
                    <PhoneOff className="w-7 h-7 text-white" />
                  </div>
                  <span className="text-sm font-medium text-gray-400">Decline</span>
                </button>
                <button onClick={answerCall} className="group flex flex-col items-center gap-3">
                  <div className="w-16 h-16 bg-[#00a884] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
                    {isVideo ? <Video className="w-7 h-7 text-white" /> : <Phone className="w-7 h-7 text-white" />}
                  </div>
                  <span className="text-sm font-medium text-gray-400">Accept</span>
                </button>
              </div>
           </div>
        </div>
      )}

      {/* 📡 --- OUTGOING CALL MODAL (WhatsApp Style) --- */}
      {isCalling && !callActive && (
        <div className="w-full h-full bg-[#0b1014] flex flex-col items-center justify-between py-20 px-10 animate-in fade-in duration-500">
           <div className="flex flex-col items-center gap-4">
              <div className="w-32 h-32 bg-[#232d36] rounded-full flex items-center justify-center overflow-hidden">
                {groupId ? <Users className="w-16 h-16 text-white" /> : <div className="text-6xl font-bold">{(targetUserName?.charAt(0).toUpperCase() || "U")}</div>}
              </div>
              <h2 className="text-4xl font-semibold">{groupId ? groupName : targetUserName}</h2>
              <p className="text-gray-400 font-medium tracking-wide">CALLING</p>
           </div>
           
           <div className="flex flex-col items-center gap-20">
              <div className="flex flex-col items-center gap-2 text-gray-500">
                 <ShieldCheck className="w-5 h-5" />
                 <span className="text-[10px] uppercase tracking-widest font-bold">End-to-end encrypted</span>
              </div>
              <button onClick={endCall} className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center shadow-lg hover:rotate-90 transition-all duration-500">
                <PhoneOff className="w-7 h-7 text-white" />
              </button>
           </div>
        </div>
      )}

      {/* 🚀 --- ACTIVE CALL SCREEN (WhatsApp Style) --- */}
      {callActive && (
        <div className={`w-full h-full flex flex-col ${isMinimized ? 'fixed bottom-8 right-8 w-80 h-48 pointer-events-auto rounded-3xl overflow-hidden shadow-2xl border border-white/10' : 'bg-[#0b1014]'}`}>
          {!groupId && (
            <audio 
              ref={(el) => {
                if (el && remoteStream && el.srcObject !== remoteStream) {
                  el.srcObject = remoteStream;
                }
              }}
              autoPlay 
            />
          )}
          
          {/* Header Info */}
          {!isMinimized && (
            <div className="p-6 flex items-center justify-between absolute top-0 left-0 w-full z-20 bg-gradient-to-b from-black/60 to-transparent">
               <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#232d36] rounded-full flex items-center justify-center overflow-hidden">
                     {groupId ? <Users className="w-5 h-5" /> : <div className="text-sm font-bold">{(callerName || targetUserName)?.charAt(0)}</div>}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{groupId ? groupName : (callerName || targetUserName)}</h3>
                    <p className="text-[10px] text-gray-400">
                      {formatDuration(callDuration)} • End-to-end encrypted
                    </p>
                  </div>
               </div>
               <button onClick={() => setIsMinimized(true)} className="p-2 hover:bg-white/10 rounded-full">
                  <Minimize2 className="w-5 h-5" />
                </button>
             </div>
           )}

           {/* Main Display Area */}
          <div className="flex-1 relative overflow-hidden">
            
            {/* Audio Mode: Large Avatar */}
            {!isVideo && participants.length === 0 && (
               <div className="w-full h-full flex flex-col items-center justify-center gap-8 bg-[#0b1014]">
                  <div className="w-48 h-48 bg-[#232d36] rounded-full flex items-center justify-center overflow-hidden shadow-2xl border-4 border-white/5">
                     <div className="text-8xl font-bold">{(callerName || targetUserName)?.charAt(0)}</div>
                  </div>
                  <div className="flex items-center gap-2 text-[#00a884]">
                     <div className="w-2 h-2 bg-[#00a884] rounded-full animate-pulse"></div>
                     <span className="text-sm font-semibold uppercase tracking-widest">In Call</span>
                  </div>
               </div>
            )}
-
            {/* Video Mode or Group Grid */}
            {(isVideo || participants.length > 0) && (
              <div className="w-full h-full relative">
                
                {/* 1-on-1 Remote Video */}
                {!groupId && isVideo && remoteStream && (
                  <div className="absolute inset-0 w-full h-full bg-black">
                    <video 
                      ref={(el) => {
                        if (el && remoteStream && el.srcObject !== remoteStream) {
                          el.srcObject = remoteStream;
                        }
                      }}
                      className="w-full h-full object-cover" 
                      autoPlay 
                      playsInline 
                    />
                    <div className="absolute top-24 left-6 bg-black/40 px-3 py-1 rounded-lg text-xs font-medium backdrop-blur-sm z-10">
                      {callerName || targetUserName}
                    </div>
                  </div>
                )}

                {/* 1-on-1 Local Video (Floating) */}
                {!groupId && isVideo && !isMinimized && (
                   <div className="absolute top-24 right-6 w-32 h-44 bg-[#1e2428] rounded-2xl overflow-hidden border border-white/10 shadow-xl z-30 group cursor-move">
                      <video ref={localVideoRef} className="w-full h-full object-cover" autoPlay playsInline muted />
                      <div className="absolute bottom-2 left-2 bg-black/40 px-2 py-0.5 rounded text-[8px] font-bold uppercase">You</div>
                   </div>
                )}

                {/* Group Participant Grid */}
                {groupId && (
                  <div className={`w-full h-full p-4 pt-24 grid gap-4 ${
                    participants.length === 0 ? 'grid-cols-1 max-w-2xl mx-auto' : 
                    participants.length === 1 ? 'grid-cols-1 md:grid-cols-2' : 
                    'grid-cols-2 lg:grid-cols-3'
                  }`}>
                    
                    {/* Others */}
                    {participants.map((p) => (
                      <div key={p.userId} className="relative rounded-3xl overflow-hidden bg-[#1e2428] border-2 border-yellow-500/30 shadow-lg aspect-video flex items-center justify-center">
                        {p.stream && isVideo ? (
                          <video 
                            className="w-full h-full object-contain bg-black" 
                            autoPlay 
                            playsInline 
                            ref={el => { 
                              if(el && p.stream && el.srcObject !== p.stream) el.srcObject = p.stream; 
                            }} 
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center gap-4 p-4">
                             <div className="w-20 h-20 bg-[#232d36] rounded-full flex items-center justify-center text-3xl font-bold">
                                {p.name?.charAt(0) || "U"}
                             </div>
                             <div className="flex gap-1 items-end h-4">
                                {[1,2,3,4,5,4,3,2,1].map((h, i) => (
                                   <div key={i} className="w-1 bg-yellow-500 rounded-full animate-pulse" style={{ height: `${h * 20}%`, animationDelay: `${i * 0.1}s` }}></div>
                                ))}
                             </div>
                          </div>
                        )}
                        <div className="absolute top-4 left-4 bg-black/40 px-3 py-1 rounded-lg text-xs font-medium backdrop-blur-sm">
                          {p.name}
                        </div>
                        {!isVideo && p.stream && <audio autoPlay ref={el => { if(el) el.srcObject = p.stream; }} />}
                      </div>
                    ))}

                    {/* Me in Group */}
                    <div className="relative rounded-3xl overflow-hidden bg-[#1e2428] border-2 border-cyan-500/30 shadow-lg aspect-video flex items-center justify-center">
                      {isVideo ? (
                        <video 
                          ref={el => {
                            if (el && localStream && el.srcObject !== localStream) el.srcObject = localStream;
                          }}
                          className="w-full h-full object-contain bg-black" 
                          autoPlay 
                          playsInline 
                          muted 
                        />
                      ) : (
                         <div className="w-full h-full flex flex-col items-center justify-center gap-4 p-4">
                            <div className="w-20 h-20 bg-[#232d36] rounded-full flex items-center justify-center text-3xl font-bold">You</div>
                            <div className="flex gap-1 items-end h-4">
                              {[1,3,5,7,5,3,1].map((h, i) => (
                                 <div key={i} className="w-1 bg-cyan-500 rounded-full animate-pulse" style={{ height: `${h * 14}%`, animationDelay: `${i * 0.15}s` }}></div>
                              ))}
                           </div>
                         </div>
                      )}
                      <div className="absolute top-4 left-4 bg-black/40 px-3 py-1 rounded-lg text-xs font-medium backdrop-blur-sm text-cyan-400">You</div>
                    </div>

                  </div>
                )}
              </div>
            )}
          </div>

          {/* Control Bar (WhatsApp Style Floating circles) */}
          {!isMinimized && (
            <div className="absolute bottom-0 left-0 w-full p-8 pb-12 flex items-center justify-center gap-6 z-40 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
               <button onClick={toggleSpeaker} title="Speaker" className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${isSpeakerOn ? 'bg-white/10 hover:bg-white/20' : 'bg-white text-black'}`}>
                 {isSpeakerOn ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
               </button>
               {isVideo && (
                 <button onClick={toggleCamera} title="Camera" className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${isCameraOff ? 'bg-red-500 text-white' : 'bg-white/10 hover:bg-white/20'}`}>
                   {isCameraOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
                 </button>
               )}
               <button onClick={toggleMute} title="Mute" className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${isMuted ? 'bg-red-500 text-white' : 'bg-white/10 hover:bg-white/20'}`}>
                 {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
               </button>
               <button onClick={endCall} title="End Call" className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center shadow-xl hover:rotate-90 hover:bg-red-700 transition-all duration-500">
                 <PhoneOff className="w-8 h-8 text-white" />
               </button>
            </div>
          )}

          {isMinimized && (
             <button onClick={() => setIsMinimized(false)} className="absolute inset-0 w-full h-full flex items-center justify-center bg-black/60 opacity-0 hover:opacity-100 transition-opacity z-10">
                <Maximize2 className="text-white w-12 h-12" />
             </button>
          )}
        </div>
      )}
    </div>
  );
}











// import { useEffect, useRef, useState, useCallback } from "react";
// import { useCallStore } from "../app/callStore";
// import { getSocket } from "../socket/socket";
// import { Phone, Video, Mic, MicOff, VideoOff, PhoneOff, Minimize2, Maximize2 } from "lucide-react";
// import ringtoneSound from "../assets/VibrateCall.mp3";
// import toast from "react-hot-toast";
// export default function CallManager() {
//   const {
//     isReceivingCall,
//     isCalling,
//     callActive,
//     callerName,
//     callerSignal,
//     callerId,
//     targetUserId,
//     targetUserName,
//     isVideo,
//     setCallState,
//     clearCallState,
//   } = useCallStore();


//   const remoteAudioRef = useRef(null);
//   const [localStream, setLocalStream] = useState(null);
//   const [remoteStream, setRemoteStream] = useState(null);

//   const [isMuted, setIsMuted] = useState(false);
//   const [isCameraOff, setIsCameraOff] = useState(false);
//   const [isMinimized, setIsMinimized] = useState(false);
//   const [autoplayBlocked, setAutoplayBlocked] = useState(false);

//   const localVideoRef = useRef(null);
//   const remoteVideoRef = useRef(null);
//   const connectionRef = useRef(null);
//   const ringtoneRef = useRef(null);
//   const vibrateInterval = useRef(null);
//   const callInitiatedRef = useRef(false);
//   const iceCandidateQueue = useRef([]);

//   // ==========================================
//   // 🎵 HELPERS (useCallback to avoid hoisting errors)
//   // ==========================================

//   const startRinging = useCallback(() => {
//     if (!ringtoneRef.current) {
//       ringtoneRef.current = new Audio(ringtoneSound);
//       ringtoneRef.current.loop = true;
//     }
//     ringtoneRef.current.play().then(() => {
//       setAutoplayBlocked(false);
//     }).catch(e => {
//       console.log("Audio block:", e);
//       setAutoplayBlocked(true);
//     });
//     if (navigator.vibrate) {
//       vibrateInterval.current = setInterval(() => {
//         navigator.vibrate([400, 200, 400]);
//       }, 1200);
//     }
//   }, []);

//   const stopRinging = useCallback(() => {
//     if (ringtoneRef.current) {
//       ringtoneRef.current.pause();
//       ringtoneRef.current.currentTime = 0;
//     }
//     if (vibrateInterval.current) clearInterval(vibrateInterval.current);
//     if (navigator.vibrate) navigator.vibrate(0);
//   }, []);

//   // const initStream = useCallback(async (videoRequired) => {
//   //   try {
//   //     const stream = await navigator.mediaDevices.getUserMedia({
//   //       video: videoRequired ? { width: 1280, height: 720, facingMode: "user" } : false,
//   //       audio: true
//   //     });
//   //     setLocalStream(stream);
//   //     return stream;
//   //   } catch (err) {
//   //     console.error("Stream access denied:", err);
//   //     return null;
//   //   }
//   // }, []);

//   const initStream = useCallback(async (videoRequired = true) => {
//     try {
//       const stream = await navigator.mediaDevices.getUserMedia({
//         video: videoRequired ? { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" } : false,
//         audio: true
//       });
  
//       console.log("🎥 Stream acquired:", stream.id, "tracks:", stream.getTracks().length);

//       // Ensure tracks are enabled
//       stream.getTracks().forEach(track => {
//         track.enabled = true;
//       });

//       setLocalStream(stream);
  
//       if (localVideoRef.current) {
//         localVideoRef.current.srcObject = stream;
//         localVideoRef.current.play().catch(e => console.log("Local play error:", e));
//       }
  
//       return stream;
//     } catch (err) {
//       console.error("Stream access denied:", err);
//       toast.error("Could not access camera/microphone");
//       return null;
//     }
//   }, []);


//   const createPeer = useCallback((stream, socket, toUserId) => {
//     const peer = new RTCPeerConnection({
//       iceServers: [
//         { urls: "stun:stun.l.google.com:19302" },
//         { urls: "stun:stun1.l.google.com:19302" },
//         { urls: "stun:stun2.l.google.com:19302" },
//       ]
//     });
  
//     // ✅ Add tracks
//     if (stream) {
//       stream.getTracks().forEach(track => {
//         peer.addTrack(track, stream);
//       });
//     }
  
//     // ✅ Handle remote stream properly
//     peer.ontrack = (event) => {
//       console.log("🎬 Remote track received:", event.track.kind);
  
//       if (event.streams && event.streams[0]) {
//         setRemoteStream(event.streams[0]);
//       } else {
//         // Fallback for browsers that don't provide event.streams
//         setRemoteStream(prev => {
//           if (prev) {
//             prev.addTrack(event.track);
//             return new MediaStream(prev.getTracks());
//           } else {
//             return new MediaStream([event.track]);
//           }
//         });
//       }
//     };
  
//     // ✅ ICE
//     peer.onicecandidate = (event) => {
//       if (event.candidate) {
//         socket.emit("ice_candidate", {
//           to: toUserId,
//           candidate: event.candidate
//         });
//       }
//     };
  
//     return peer;
//   }, []);




//   const endCallCleanup = useCallback(() => {
//     // 📝 LOG CALL IN CHAT BEFORE CLEANUP
//     if (callActive || isCalling) {
//       const socket = getSocket();
//       const targetId = callerId || targetUserId;
//       const groupId = useCallStore.getState().groupId;
//       if (socket && (targetId || groupId)) {
//         socket.emit("send_message", {
//           receiverId: targetId || null,
//           groupId: groupId || null,
//           message: `📞 ${isVideo ? 'Video' : 'Audio'} call ended`,
//           type: "call_log", // Custom type for UI styling
//           clientTime: new Date(),
//         });
//       }
//     }

//     // if (localStream) localStream.getTracks().forEach(track => track.stop());
//     if (localStream) {
//       localStream.getTracks().forEach(track => track.stop());
//     }
    
//     if (remoteStream) {
//       remoteStream.getTracks().forEach(track => track.stop());
//     }
//     if (connectionRef.current) {
//       connectionRef.current.close();
//       connectionRef.current = null;
//     }
//     setLocalStream(null);
//     setRemoteStream(null);
//     iceCandidateQueue.current = [];
//     clearCallState();
//     stopRinging();
//     callInitiatedRef.current = false;
//   }, [callActive, isCalling, callerId, targetUserId, isVideo, localStream, clearCallState, stopRinging]);

//   const initiateCallFlow = useCallback(async () => {
//     const socket = getSocket();
//     if (!socket) return;
//     try {
//       const stream = await initStream(isVideo);
//       if (!stream) {
//         clearCallState();
//         return;
//       }
//       const peer = createPeer(stream, socket, targetUserId);
//       connectionRef.current = peer;
//       const offer = await peer.createOffer();
//       await peer.setLocalDescription(offer);
//       const user = JSON.parse(localStorage.getItem("user") || "{}");
//       socket.emit("call_user", {
//         userToCall: targetUserId || null,
//         groupToCall: useCallStore.getState().groupId || null,
//         signalData: offer,
//         from: user.id,
//         name: `${user.first_name} ${user.last_name || ""}`,
//         isVideo: isVideo,
//       });
//     } catch (err) {
//       console.error("Initiate error:", err);
//       endCallCleanup();
//     }
//   }, [isVideo, targetUserId, clearCallState, initStream, createPeer, endCallCleanup]);

//   // ==========================================
//   // 🧨 EFFECTS
//   // ==========================================

//   useEffect(() => {
//     const socket = getSocket();
//     if (!socket) return;

//     const handleIncomingCall = (data) => {
//       console.log("☎️ Incoming call from:", data.from);
//       setCallState({
//         isReceivingCall: true,
//         callerId: data.from,
//         callerName: data.groupId ? `Group: ${data.name}` : data.name,
//         callerSignal: data.signal,
//         isVideo: data.isVideo,
//         groupId: data.groupId || null,
//       });
//       startRinging();
//     };

//     const handleCallAccepted = async (signal) => {
//       console.log("✅ Call accepted! Establishing connection...");
//       stopRinging();
//       setCallState({ callActive: true, isCalling: false });
//       if (connectionRef.current) {
//         try {
//           await connectionRef.current.setRemoteDescription(new RTCSessionDescription(signal));
//           // Process queued ICE candidates
//           while (iceCandidateQueue.current.length > 0) {
//             const candidate = iceCandidateQueue.current.shift();
//             await connectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
//           }
//         } catch (e) {
//           console.error("Error setting remote description:", e);
//         }
//       }
//     };

//     const handleCallRejected = () => {
//       stopRinging();
//       endCallCleanup();
//     };

//     const handleCallEnded = () => {
//       stopRinging();
//       endCallCleanup();
//     };

//     const handleIceCandidate = async (candidate) => {
//       if (connectionRef.current && connectionRef.current.remoteDescription) {
//         try {
//           await connectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
//         } catch (e) {
//           console.error("Error adding ice candidate:", e);
//         }
//       } else {
//         iceCandidateQueue.current.push(candidate);
//       }
//     };

//     socket.on("incoming_call", handleIncomingCall);
//     socket.on("call_accepted", handleCallAccepted);
//     socket.on("call_rejected", handleCallRejected);
//     socket.on("call_ended", handleCallEnded);
//     socket.on("ice_candidate", handleIceCandidate);

//     return () => {
//       socket.off("incoming_call", handleIncomingCall);
//       socket.off("call_accepted", handleCallAccepted);
//       socket.off("call_rejected", handleCallRejected);
//       socket.off("call_ended", handleCallEnded);
//       socket.off("ice_candidate", handleIceCandidate);
//     };
//   }, [setCallState, stopRinging, startRinging, endCallCleanup]);

//   useEffect(() => {
//     if (isCalling && !callActive && !callInitiatedRef.current) {
//       callInitiatedRef.current = true;
//       initiateCallFlow();
//     } else if (!isCalling && !callActive) {
//       callInitiatedRef.current = false;
//     }
//   }, [isCalling, callActive, initiateCallFlow]);



 
//   // useEffect(() => {
//   //   if (localVideoRef.current && localStream) {
//   //     localVideoRef.current.srcObject = localStream;
  
//   //     const playPromise = localVideoRef.current.play();
//   //     if (playPromise !== undefined) {
//   //       playPromise.catch((err) => {
//   //         console.log("❌ Local video autoplay blocked:", err);
//   //       });
//   //     }
//   //   }
//   // }, [localStream]);

//   useEffect(() => {
//     if (localVideoRef.current && localStream) {



//           // 🔥 ADD HERE
//     console.log(
//       "LOCAL VIDEO TRACK ENABLED:",
//       localStream?.getVideoTracks()[0]?.enabled
//     );

//       console.log("✅ Attaching LOCAL stream");
  
//       localVideoRef.current.srcObject = localStream;
//       localVideoRef.current.muted = true; // 🔥 important
//       localVideoRef.current.playsInline = true;
  
//       const playPromise = localVideoRef.current.play();
//       if (playPromise !== undefined) {
//         playPromise.catch((err) => {
//           console.log("❌ Local video autoplay blocked:", err);
//         });
//       }
//     }
//   }, [localStream, callActive, isMinimized]);


//   useEffect(() => {
//     if (!remoteStream || !callActive) return;
  
//     console.log("🔊 Syncing remote stream to elements...");
  
//     const attachStream = () => {
//       // 🎥 VIDEO
//       if (remoteVideoRef.current) {
//         if (remoteVideoRef.current.srcObject !== remoteStream) {
//           console.log("✅ Attaching remote stream to VIDEO");
//           remoteVideoRef.current.srcObject = remoteStream;
//         }
//         remoteVideoRef.current.play().catch(e => console.log("Remote video play error:", e));
//       }
  
//       // 🔊 AUDIO
//       if (remoteAudioRef.current) {
//         if (remoteAudioRef.current.srcObject !== remoteStream) {
//           console.log("✅ Attaching remote stream to AUDIO");
//           remoteAudioRef.current.srcObject = remoteStream;
//         }
//         remoteAudioRef.current.play().catch(e => console.log("Remote audio play error:", e));
//       }
//     };

//     // Small delay to ensure refs are populated after re-render
//     const timeoutId = setTimeout(attachStream, 100);
//     return () => clearTimeout(timeoutId);
  
//   }, [remoteStream, callActive, isVideo, isMinimized]);



//   // ==========================================
//   // 🎮 ACTIONS
//   // ==========================================

//   const answerCall = async () => {
//     stopRinging();
//     const stream = await initStream(isVideo);
//     if (!stream) return;
//     setCallState({ callActive: true, isReceivingCall: false });
//     const socket = getSocket();
//     const peer = createPeer(stream, socket, callerId);
//     connectionRef.current = peer;
//     try {
//       await peer.setRemoteDescription(new RTCSessionDescription(callerSignal));
//       const answer = await peer.createAnswer();
//       await peer.setLocalDescription(answer);
//       socket.emit("make_answer", { to: callerId, signal: answer });

//       // Process queued candidates after remote description is set
//       while (iceCandidateQueue.current.length > 0) {
//         const candidate = iceCandidateQueue.current.shift();
//         await peer.addIceCandidate(new RTCIceCandidate(candidate));
//       }
//     } catch (e) {
//       console.error("Answer error:", e);
//       endCallCleanup();
//     }
//   };

//   const rejectCall = () => {
//     stopRinging();
//     const socket = getSocket();
//     if (socket) socket.emit("reject_call", { to: callerId });
//     clearCallState();
//   };

//   const endCall = () => {
//     const socket = getSocket();
//     const targetId = callerId || targetUserId;
//     const groupId = useCallStore.getState().groupId;

//     if (socket) {
//       if (targetId) socket.emit("end_call", { to: targetId });
//       else if (groupId) socket.emit("end_call", { groupId });
//     }
//     endCallCleanup();
//   };

//   const toggleMute = () => {
//     if (localStream) {
//       localStream.getAudioTracks().forEach(track => {
//         track.enabled = !track.enabled;

//         console.log("VIDEO TRACK ENABLED:", track.enabled);
//         setIsMuted(!track.enabled);
//       });
//     }
//   };

//   // const toggleCamera = () => {
//   //   if (localStream) {
//   //     console.log("LOCAL STREAM TRACKS:", localStream.getVideoTracks());

//   //     localStream.getVideoTracks().forEach(track => {
//   //       track.enabled = !track.enabled;
//   //       setIsCameraOff(!track.enabled);
//   //     });
//   //   }
//   // };




//   const toggleCamera = () => {
//     if (!localStream) return;
  
//     const videoTrack = localStream.getVideoTracks()[0];
//     if (!videoTrack) return;
  
//     const newState = !videoTrack.enabled;
//     videoTrack.enabled = newState;
  
//     console.log("🎥 Camera enabled:", newState);
  
//     setIsCameraOff(!newState); // UI follows real state
//   };

//   if (!isCalling && !isReceivingCall && !callActive) return null;

//   return (
//     <div className={`fixed inset-0 z-[2000000] transition-all duration-700 flex items-center justify-center ${isMinimized ? 'pointer-events-none' : 'bg-[#050508]/70 backdrop-blur-[32px]'}`}>

//       {/* 🔮 --- INCOMING CALL MODAL --- */}
//       {isReceivingCall && !callActive && (
//         <div className="bg-[#12141c]/90 border border-white/10 rounded-[3rem] p-12 flex flex-col items-center gap-8 shadow-[0_32px_128px_-16px_rgba(0,0,0,0.9)] w-[400px] animate-in zoom-in-95 duration-500">
//           <div className="relative">
//             <div className="absolute inset-0 bg-indigo-500 rounded-full blur-[50px] opacity-30 animate-pulse"></div>
//             <div className="w-32 h-32 rounded-full p-1 bg-gradient-to-tr from-indigo-600 via-purple-500 to-pink-500 animate-spin-slow">
//               <div className="w-full h-full bg-[#12141c] rounded-full flex justify-center items-center text-6xl text-white font-black border-2 border-[#12141c]">
//                 {callerName?.charAt(0).toUpperCase() || "U"}
//               </div>
//             </div>
//             <div className="absolute -bottom-1 -right-1 w-10 h-10 bg-emerald-500 rounded-full border-4 border-[#12141c] flex items-center justify-center animate-bounce shadow-xl shadow-emerald-500/20">
//               <Phone className="text-white w-5 h-5 fill-white" />
//             </div>
//           </div>
//           <div className="text-center">
//             <h2 className="text-4xl font-black text-white tracking-tight mb-3">{callerName || "Unknown"}</h2>
//             {autoplayBlocked && (
//               <button
//                 onClick={() => {
//                   ringtoneRef.current?.play();
//                   setAutoplayBlocked(false);
//                 }}
//                 className="mb-4 text-[10px] text-indigo-400 font-bold uppercase animate-pulse underline"
//               >
//                 Click to enable ringtone
//               </button>
//             )}
//             <p className="text-purple-400 font-black uppercase tracking-[0.2em] text-[10px] bg-purple-500/10 px-4 py-1.5 rounded-full border border-purple-500/20">
//               {isVideo ? "Incoming Video Request" : "Incoming Audio Request"}
//             </p>
//           </div>
//           <div className="flex gap-12 mt-2">
//             <button onClick={rejectCall} className="group flex flex-col items-center gap-3">
//               <div className="w-16 h-16 bg-red-500/10 hover:bg-red-500 rounded-2xl flex items-center justify-center text-red-500 hover:text-white transition-all duration-300 border border-red-500/20 shadow-lg shadow-red-500/10">
//                 <PhoneOff className="w-7 h-7" />
//               </div>
//               <span className="text-[10px] text-white/40 uppercase font-black tracking-widest">Decline</span>
//             </button>
//             <button onClick={answerCall} className="group flex flex-col items-center gap-3">
//               <div className="w-16 h-16 bg-emerald-500/10 hover:bg-emerald-500 rounded-2xl flex items-center justify-center text-emerald-500 hover:text-white transition-all duration-300 border border-emerald-500/20 shadow-lg shadow-emerald-500/10">
//                 {isVideo ? <Video className="w-7 h-7" /> : <Phone className="w-7 h-7" />}
//               </div>
//               <span className="text-[10px] text-white/40 uppercase font-black tracking-widest">Accept</span>
//             </button>
//           </div>
//         </div>
//       )}

//       {/* 📡 --- OUTGOING CALL MODAL --- */}
//       {isCalling && !callActive && (
//         <div className="bg-[#12141c]/90 border border-white/10 rounded-[3rem] p-12 flex flex-col items-center gap-8 shadow-[0_32px_128px_-16px_rgba(0,0,0,0.9)] w-[400px] animate-in slide-in-from-bottom-20 duration-500">
//           <div className="relative">
//             <div className="absolute inset-0 bg-blue-500 rounded-full blur-[50px] opacity-30 animate-pulse"></div>
//             <div className="w-32 h-32 rounded-full border-2 border-dashed border-blue-500/40 flex justify-center items-center animate-spin-slow">
//               <div className="w-24 h-24 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-full flex justify-center items-center text-5xl text-white font-black shadow-2xl">
//                 {targetUserName?.charAt(0).toUpperCase() || "U"}
//               </div>
//             </div>
//           </div>
//           <div className="text-center">
//             <h2 className="text-4xl font-black text-white tracking-tight mb-2">Connecting...</h2>
//             <p className="text-white/40 text-xs font-black uppercase tracking-widest italic">{targetUserName || "Secure User"}</p>
//           </div>
//           <button onClick={endCall} className="w-16 h-16 bg-red-600 hover:bg-red-700 hover:rotate-90 rounded-2xl flex items-center justify-center text-white transition-all duration-500 shadow-2xl shadow-red-600/30">
//             <PhoneOff className="w-7 h-7" />
//           </button>
//         </div>
//       )}

//       {/* 🚀 --- ACTIVE CALL SCREEN --- */}
//       {callActive && (
//         <div className={`transition-all duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] z-[1000001] ${isMinimized ? 'fixed bottom-8 right-8 w-80 h-48 pointer-events-auto rounded-[2rem] overflow-hidden shadow-2xl border border-white/20' : 'w-full h-full max-w-7xl mx-auto flex flex-col p-6'}`}>
//           <div className="flex-1 relative bg-[#08090a] rounded-[3rem] overflow-hidden shadow-[0_64px_128px_-20px_rgba(0,0,0,0.9)] border border-white/10">
//             <div className="w-full h-full flex flex-col items-center justify-center overflow-hidden">
//               {isVideo ? (
//                 <video ref={remoteVideoRef} className="w-full h-full object-cover scale-[1.02]" autoPlay playsInline controls={false} />
//               ) : (
//                 <div className="flex flex-col items-center gap-10">
//                   <div className="relative">
//                     <div className="absolute inset-0 bg-indigo-500 rounded-full blur-[80px] opacity-20 animate-pulse"></div>
//                     <div className="w-48 h-48 bg-gradient-to-tr from-[#1e1b4b] via-[#312e81] to-[#3730a3] rounded-full flex items-center justify-center text-8xl text-white font-black shadow-2xl border-4 border-white/10">
//                       {(callerName || targetUserName)?.charAt(0) || "U"}
//                     </div>
//                     <div className="absolute -bottom-2 right-1/2 translate-x-1/2 px-6 py-2 bg-indigo-600 rounded-2xl text-[10px] text-white font-black uppercase tracking-[0.3em] shadow-2xl border-4 border-[#08090a]">Secured</div>
//                   </div>
//                   <div className="text-center">
//                     <h3 className="text-5xl font-black text-white mb-2 tracking-tighter">{callerName || targetUserName || "User"}</h3>
//                     <p className="text-indigo-400 font-black uppercase tracking-[0.3em] text-[10px] animate-pulse">End-to-End Encrypted</p>
//                   </div>
//                   {/* <audio ref={remoteVideoRef} autoPlay /> */}
//                   <audio ref={remoteAudioRef} autoPlay />
//                 </div>
//               )}
//             </div>
//             {isVideo && !isMinimized && (
//               <div className="absolute top-10 right-10 w-48 h-64 bg-black/40 backdrop-blur-3xl rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/20 group cursor-move hover:border-indigo-500/50 transition-colors">
//                 <video ref={localVideoRef} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" autoPlay playsInline muted />
//                 <div className="absolute top-4 left-4 bg-black/40 px-2 py-0.5 rounded-lg text-[8px] text-white/60 font-black uppercase tracking-widest">You</div>
//               </div>
//             )}
//             {!isMinimized ? (
//               <button onClick={() => setIsMinimized(true)} className="absolute top-10 left-10 p-4 bg-white/5 hover:bg-white/10 text-white rounded-2xl transition-all border border-white/10 group">
//                 <Minimize2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
//               </button>
//             ) : (
//               <button onClick={() => setIsMinimized(false)} className="absolute inset-0 w-full h-full flex items-center justify-center bg-black/60 opacity-0 hover:opacity-100 transition-opacity z-10">
//                 <Maximize2 className="text-white w-12 h-12" />
//               </button>
//             )}
//           </div>
//           {!isMinimized && (
//             <div className="h-36 flex items-center justify-center gap-12 relative mt-4">
//               <div className="bg-[#12141c]/60 backdrop-blur-[48px] px-16 py-7 rounded-[3rem] border border-white/10 flex items-center gap-12 shadow-[0_24px_48px_rgba(0,0,0,0.6)]">
//                 <button onClick={toggleMute} className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${isMuted ? 'bg-red-500 text-white scale-110' : 'bg-white/5 text-white hover:bg-white/15'}`}>
//                   {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
//                 </button>
//                 <button onClick={endCall} className="w-20 h-20 bg-red-600 hover:bg-red-700 hover:rotate-90 rounded-[2rem] flex items-center justify-center text-white transition-all duration-500 shadow-2xl shadow-red-600/40">
//                   <PhoneOff className="w-9 h-9" />
//                 </button>
//                 {isVideo && (
//                   <button onClick={toggleCamera} className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${isCameraOff ? 'bg-red-500 text-white scale-110' : 'bg-white/5 text-white hover:bg-white/15'}`}>
//                     {isCameraOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
//                   </button>
//                 )}
//               </div>
//               <div className="absolute right-10 flex items-center gap-4 px-8 py-4 bg-white/5 rounded-3xl border border-white/10 backdrop-blur-md">
//                 <div className="flex flex-col items-end">
//                   <span className="text-[10px] text-white/30 uppercase font-black tracking-widest leading-none mb-1">Channel</span>
//                   <span className="text-xs text-emerald-400 font-black tracking-tight">Active_P2P</span>
//                 </div>
//                 <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_12px_rgba(16,185,129,0.8)]"></div>
//               </div>
//             </div>
//           )}
//         </div>
//       )}
//       <style>{`
//         @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
//         .animate-spin-slow { animation: spin-slow 20s linear infinite; }
//       `}</style>
//     </div>
//   );
// }





// // import { useEffect, useRef, useState, useCallback } from "react";
// // import { useCallStore } from "../app/callStore";
// // import { getSocket } from "../socket/socket";
// // import { Phone, Video, Mic, MicOff, VideoOff, PhoneOff, Minimize2, Maximize2 } from "lucide-react";
// // import ringtoneSound from "../assets/VibrateCall.mp3";

// // export default function CallManager() {
// //   const {
// //     isReceivingCall,
// //     isCalling,
// //     callActive,
// //     callerName,
// //     callerSignal,
// //     callerId,
// //     targetUserId,
// //     targetUserName,
// //     isVideo,
// //     setCallState,
// //     clearCallState,
// //   } = useCallStore();


// //   const remoteAudioRef = useRef(null);
// //   const [localStream, setLocalStream] = useState(null);
// //   const [remoteStream, setRemoteStream] = useState(null);

// //   const [isMuted, setIsMuted] = useState(false);
// //   const [isCameraOff, setIsCameraOff] = useState(false);
// //   const [isMinimized, setIsMinimized] = useState(false);
// //   const [autoplayBlocked, setAutoplayBlocked] = useState(false);

// //   const localVideoRef = useRef(null);
// //   const remoteVideoRef = useRef(null);
// //   const connectionRef = useRef(null);
// //   const ringtoneRef = useRef(null);
// //   const vibrateInterval = useRef(null);
// //   const callInitiatedRef = useRef(false);

// //   // ==========================================
// //   // 🎵 HELPERS (useCallback to avoid hoisting errors)
// //   // ==========================================

// //   const startRinging = useCallback(() => {
// //     if (!ringtoneRef.current) {
// //       ringtoneRef.current = new Audio(ringtoneSound);
// //       ringtoneRef.current.loop = true;
// //     }
// //     ringtoneRef.current.play().then(() => {
// //       setAutoplayBlocked(false);
// //     }).catch(e => {
// //       console.log("Audio block:", e);
// //       setAutoplayBlocked(true);
// //     });
// //     if (navigator.vibrate) {
// //       vibrateInterval.current = setInterval(() => {
// //         navigator.vibrate([400, 200, 400]);
// //       }, 1200);
// //     }
// //   }, []);

// //   const stopRinging = useCallback(() => {
// //     if (ringtoneRef.current) {
// //       ringtoneRef.current.pause();
// //       ringtoneRef.current.currentTime = 0;
// //     }
// //     if (vibrateInterval.current) clearInterval(vibrateInterval.current);
// //     if (navigator.vibrate) navigator.vibrate(0);
// //   }, []);

// //   // const initStream = useCallback(async (videoRequired) => {
// //   //   try {
// //   //     const stream = await navigator.mediaDevices.getUserMedia({
// //   //       video: videoRequired ? { width: 1280, height: 720, facingMode: "user" } : false,
// //   //       audio: true
// //   //     });
// //   //     setLocalStream(stream);
// //   //     return stream;
// //   //   } catch (err) {
// //   //     console.error("Stream access denied:", err);
// //   //     return null;
// //   //   }
// //   // }, []);

// //   const initStream = useCallback(async () => {
// //     try {
// //       const stream = await navigator.mediaDevices.getUserMedia({
// //         video: true,
// //         audio: true
// //       });
  
// //       console.log("🎥 Tracks:", stream.getTracks());

// //       // ✅ FORCE ENABLE CAMERA
// // stream.getVideoTracks().forEach(track => {
// //   track.enabled = true;
// // });

  
// //       setLocalStream(stream);
  
// //       if (localVideoRef.current) {
// //         localVideoRef.current.srcObject = stream;
// //         localVideoRef.current.play().catch(() => {});
// //       }
  
// //       return stream;
// //     } catch (err) {
// //       console.error("Stream access denied:", err);
// //       return null;
// //     }
// //   }, []);


// //   const createPeer = useCallback((stream, socket, toUserId) => {
// //     const peer = new RTCPeerConnection({
// //       iceServers: [{ urls: "stun:stun.l.google.com:19302" }]
// //     });
  
// //     // ✅ Add tracks
// //     stream.getTracks().forEach(track => {
// //       peer.addTrack(track, stream);
// //     });
  
// //     // ✅ FIX: handle remote stream properly
// //     peer.ontrack = (event) => {
// //       console.log("🎬 Remote track:", event.streams);
  
// //       if (event.streams && event.streams[0]) {
// //         setRemoteStream(event.streams[0]);
// //       }
// //     };
  
// //     // ✅ ICE
// //     peer.onicecandidate = (event) => {
// //       if (event.candidate) {
// //         socket.emit("ice_candidate", {
// //           to: toUserId,
// //           candidate: event.candidate
// //         });
// //       }
// //     };
  
// //     return peer;
// //   }, []);




// //   const endCallCleanup = useCallback(() => {
// //     // 📝 LOG CALL IN CHAT BEFORE CLEANUP
// //     if (callActive || isCalling) {
// //       const socket = getSocket();
// //       const targetId = callerId || targetUserId;
// //       const groupId = useCallStore.getState().groupId;
// //       if (socket && (targetId || groupId)) {
// //         socket.emit("send_message", {
// //           receiverId: targetId || null,
// //           groupId: groupId || null,
// //           message: `📞 ${isVideo ? 'Video' : 'Audio'} call ended`,
// //           type: "call_log", // Custom type for UI styling
// //           clientTime: new Date(),
// //         });
// //       }
// //     }

// //     // if (localStream) localStream.getTracks().forEach(track => track.stop());
// //     if (localStream) {
// //       localStream.getTracks().forEach(track => track.stop());
// //     }
    
// //     if (remoteStream) {
// //       remoteStream.getTracks().forEach(track => track.stop());
// //     }
// //     if (connectionRef.current) {
// //       connectionRef.current.close();
// //       connectionRef.current = null;
// //     }
// //     setLocalStream(null);
// //     setRemoteStream(null);
// //     clearCallState();
// //     stopRinging();
// //     callInitiatedRef.current = false;
// //   }, [callActive, isCalling, callerId, targetUserId, isVideo, localStream, clearCallState, stopRinging]);

// //   const initiateCallFlow = useCallback(async () => {
// //     const socket = getSocket();
// //     if (!socket) return;
// //     try {
// //       const stream = await initStream(isVideo);
// //       if (!stream) {
// //         clearCallState();
// //         return;
// //       }
// //       const peer = createPeer(stream, socket, targetUserId);
// //       connectionRef.current = peer;
// //       const offer = await peer.createOffer();
// //       await peer.setLocalDescription(offer);
// //       const user = JSON.parse(localStorage.getItem("user") || "{}");
// //       socket.emit("call_user", {
// //         userToCall: targetUserId || null,
// //         groupToCall: useCallStore.getState().groupId || null,
// //         signalData: offer,
// //         from: user.id,
// //         name: `${user.first_name} ${user.last_name || ""}`,
// //         isVideo: isVideo,
// //       });
// //     } catch (err) {
// //       console.error("Initiate error:", err);
// //       endCallCleanup();
// //     }
// //   }, [isVideo, targetUserId, clearCallState, initStream, createPeer, endCallCleanup]);

// //   // ==========================================
// //   // 🧨 EFFECTS
// //   // ==========================================

// //   useEffect(() => {
// //     const socket = getSocket();
// //     if (!socket) return;

// //     const handleIncomingCall = (data) => {
// //       console.log("☎️ Incoming call from:", data.from);
// //       setCallState({
// //         isReceivingCall: true,
// //         callerId: data.from,
// //         callerName: data.groupId ? `Group: ${data.name}` : data.name,
// //         callerSignal: data.signal,
// //         isVideo: data.isVideo,
// //         groupId: data.groupId || null,
// //       });
// //       startRinging();
// //     };

// //     const handleCallAccepted = async (signal) => {
// //       console.log("✅ Call accepted! Establishing connection...");
// //       stopRinging();
// //       setCallState({ callActive: true, isCalling: false });
// //       if (connectionRef.current) {
// //         try {
// //           await connectionRef.current.setRemoteDescription(new RTCSessionDescription(signal));
// //         } catch (e) {
// //           console.error("Error setting remote description:", e);
// //         }
// //       }
// //     };

// //     const handleCallRejected = () => {
// //       stopRinging();
// //       endCallCleanup();
// //     };

// //     const handleCallEnded = () => {
// //       stopRinging();
// //       endCallCleanup();
// //     };

// //     const handleIceCandidate = async (candidate) => {
// //       if (connectionRef.current && candidate) {
// //         try {
// //           await connectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
// //         } catch (e) {
// //           console.error("Error adding ice candidate:", e);
// //         }
// //       }
// //     };

// //     socket.on("incoming_call", handleIncomingCall);
// //     socket.on("call_accepted", handleCallAccepted);
// //     socket.on("call_rejected", handleCallRejected);
// //     socket.on("call_ended", handleCallEnded);
// //     socket.on("ice_candidate", handleIceCandidate);

// //     return () => {
// //       socket.off("incoming_call", handleIncomingCall);
// //       socket.off("call_accepted", handleCallAccepted);
// //       socket.off("call_rejected", handleCallRejected);
// //       socket.off("call_ended", handleCallEnded);
// //       socket.off("ice_candidate", handleIceCandidate);
// //     };
// //   }, [setCallState, stopRinging, startRinging, endCallCleanup]);

// //   useEffect(() => {
// //     if (isCalling && !callActive && !callInitiatedRef.current) {
// //       callInitiatedRef.current = true;
// //       initiateCallFlow();
// //     } else if (!isCalling && !callActive) {
// //       callInitiatedRef.current = false;
// //     }
// //   }, [isCalling, callActive, initiateCallFlow]);



 
// //   // useEffect(() => {
// //   //   if (localVideoRef.current && localStream) {
// //   //     localVideoRef.current.srcObject = localStream;
  
// //   //     const playPromise = localVideoRef.current.play();
// //   //     if (playPromise !== undefined) {
// //   //       playPromise.catch((err) => {
// //   //         console.log("❌ Local video autoplay blocked:", err);
// //   //       });
// //   //     }
// //   //   }
// //   // }, [localStream]);

// //   useEffect(() => {
// //     if (localVideoRef.current && localStream) {



// //           // 🔥 ADD HERE
// //     console.log(
// //       "LOCAL VIDEO TRACK ENABLED:",
// //       localStream?.getVideoTracks()[0]?.enabled
// //     );

// //       console.log("✅ Attaching LOCAL stream");
  
// //       localVideoRef.current.srcObject = localStream;
// //       localVideoRef.current.muted = true; // 🔥 important
// //       localVideoRef.current.playsInline = true;
  
// //       const playPromise = localVideoRef.current.play();
// //       if (playPromise !== undefined) {
// //         playPromise.catch((err) => {
// //           console.log("❌ Local video autoplay blocked:", err);
// //         });
// //       }
// //     }
// //   }, [localStream, callActive, isMinimized]);


// //   useEffect(() => {
// //     if (!remoteStream) return;
  
// //     console.log("🔊 Attaching remote stream...");
  
// //     // 🎥 VIDEO
// //     if (remoteVideoRef.current) {
// //       remoteVideoRef.current.srcObject = remoteStream;
// //       remoteVideoRef.current.play().catch(() => {});
// //     }
  
// //     // 🔊 AUDIO (CRITICAL FIX)
// //     if (remoteAudioRef.current) {
// //       remoteAudioRef.current.srcObject = remoteStream;
// //       remoteAudioRef.current.play().catch(() => {});
// //     }
  
// //   }, [remoteStream, callActive, isMinimized]);



// //   // ==========================================
// //   // 🎮 ACTIONS
// //   // ==========================================

// //   const answerCall = async () => {
// //     stopRinging();
// //     const stream = await initStream(isVideo);
// //     if (!stream) return;
// //     setCallState({ callActive: true, isReceivingCall: false });
// //     const socket = getSocket();
// //     const peer = createPeer(stream, socket, callerId);
// //     connectionRef.current = peer;
// //     try {
// //       await peer.setRemoteDescription(new RTCSessionDescription(callerSignal));
// //       const answer = await peer.createAnswer();
// //       await peer.setLocalDescription(answer);
// //       socket.emit("make_answer", { to: callerId, signal: answer });
// //     } catch (e) {
// //       console.error("Answer error:", e);
// //       endCallCleanup();
// //     }
// //   };

// //   const rejectCall = () => {
// //     stopRinging();
// //     const socket = getSocket();
// //     if (socket) socket.emit("reject_call", { to: callerId });
// //     clearCallState();
// //   };

// //   const endCall = () => {
// //     const socket = getSocket();
// //     const targetId = callerId || targetUserId;
// //     const groupId = useCallStore.getState().groupId;

// //     if (socket) {
// //       if (targetId) socket.emit("end_call", { to: targetId });
// //       else if (groupId) socket.emit("end_call", { groupId });
// //     }
// //     endCallCleanup();
// //   };

// //   const toggleMute = () => {
// //     if (localStream) {
// //       localStream.getAudioTracks().forEach(track => {
// //         track.enabled = !track.enabled;

// //         console.log("VIDEO TRACK ENABLED:", track.enabled);
// //         setIsMuted(!track.enabled);
// //       });
// //     }
// //   };

// //   // const toggleCamera = () => {
// //   //   if (localStream) {
// //   //     console.log("LOCAL STREAM TRACKS:", localStream.getVideoTracks());

// //   //     localStream.getVideoTracks().forEach(track => {
// //   //       track.enabled = !track.enabled;
// //   //       setIsCameraOff(!track.enabled);
// //   //     });
// //   //   }
// //   // };




// //   const toggleCamera = () => {
// //     if (!localStream) return;
  
// //     const videoTrack = localStream.getVideoTracks()[0];
// //     if (!videoTrack) return;
  
// //     const newState = !videoTrack.enabled;
// //     videoTrack.enabled = newState;
  
// //     console.log("🎥 Camera enabled:", newState);
  
// //     setIsCameraOff(!newState); // UI follows real state
// //   };

// //   if (!isCalling && !isReceivingCall && !callActive) return null;

// //   return (
// //     <div className={`fixed inset-0 z-[2000000] transition-all duration-700 flex items-center justify-center ${isMinimized ? 'pointer-events-none' : 'bg-[#050508]/70 backdrop-blur-[32px]'}`}>

// //       {/* 🔮 --- INCOMING CALL MODAL --- */}
// //       {isReceivingCall && !callActive && (
// //         <div className="bg-[#12141c]/90 border border-white/10 rounded-[3rem] p-12 flex flex-col items-center gap-8 shadow-[0_32px_128px_-16px_rgba(0,0,0,0.9)] w-[400px] animate-in zoom-in-95 duration-500">
// //           <div className="relative">
// //             <div className="absolute inset-0 bg-indigo-500 rounded-full blur-[50px] opacity-30 animate-pulse"></div>
// //             <div className="w-32 h-32 rounded-full p-1 bg-gradient-to-tr from-indigo-600 via-purple-500 to-pink-500 animate-spin-slow">
// //               <div className="w-full h-full bg-[#12141c] rounded-full flex justify-center items-center text-6xl text-white font-black border-2 border-[#12141c]">
// //                 {callerName?.charAt(0).toUpperCase() || "U"}
// //               </div>
// //             </div>
// //             <div className="absolute -bottom-1 -right-1 w-10 h-10 bg-emerald-500 rounded-full border-4 border-[#12141c] flex items-center justify-center animate-bounce shadow-xl shadow-emerald-500/20">
// //               <Phone className="text-white w-5 h-5 fill-white" />
// //             </div>
// //           </div>
// //           <div className="text-center">
// //             <h2 className="text-4xl font-black text-white tracking-tight mb-3">{callerName || "Unknown"}</h2>
// //             {autoplayBlocked && (
// //               <button
// //                 onClick={() => {
// //                   ringtoneRef.current?.play();
// //                   setAutoplayBlocked(false);
// //                 }}
// //                 className="mb-4 text-[10px] text-indigo-400 font-bold uppercase animate-pulse underline"
// //               >
// //                 Click to enable ringtone
// //               </button>
// //             )}
// //             <p className="text-purple-400 font-black uppercase tracking-[0.2em] text-[10px] bg-purple-500/10 px-4 py-1.5 rounded-full border border-purple-500/20">
// //               {isVideo ? "Incoming Video Request" : "Incoming Audio Request"}
// //             </p>
// //           </div>
// //           <div className="flex gap-12 mt-2">
// //             <button onClick={rejectCall} className="group flex flex-col items-center gap-3">
// //               <div className="w-16 h-16 bg-red-500/10 hover:bg-red-500 rounded-2xl flex items-center justify-center text-red-500 hover:text-white transition-all duration-300 border border-red-500/20 shadow-lg shadow-red-500/10">
// //                 <PhoneOff className="w-7 h-7" />
// //               </div>
// //               <span className="text-[10px] text-white/40 uppercase font-black tracking-widest">Decline</span>
// //             </button>
// //             <button onClick={answerCall} className="group flex flex-col items-center gap-3">
// //               <div className="w-16 h-16 bg-emerald-500/10 hover:bg-emerald-500 rounded-2xl flex items-center justify-center text-emerald-500 hover:text-white transition-all duration-300 border border-emerald-500/20 shadow-lg shadow-emerald-500/10">
// //                 {isVideo ? <Video className="w-7 h-7" /> : <Phone className="w-7 h-7" />}
// //               </div>
// //               <span className="text-[10px] text-white/40 uppercase font-black tracking-widest">Accept</span>
// //             </button>
// //           </div>
// //         </div>
// //       )}

// //       {/* 📡 --- OUTGOING CALL MODAL --- */}
// //       {isCalling && !callActive && (
// //         <div className="bg-[#12141c]/90 border border-white/10 rounded-[3rem] p-12 flex flex-col items-center gap-8 shadow-[0_32px_128px_-16px_rgba(0,0,0,0.9)] w-[400px] animate-in slide-in-from-bottom-20 duration-500">
// //           <div className="relative">
// //             <div className="absolute inset-0 bg-blue-500 rounded-full blur-[50px] opacity-30 animate-pulse"></div>
// //             <div className="w-32 h-32 rounded-full border-2 border-dashed border-blue-500/40 flex justify-center items-center animate-spin-slow">
// //               <div className="w-24 h-24 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-full flex justify-center items-center text-5xl text-white font-black shadow-2xl">
// //                 {targetUserName?.charAt(0).toUpperCase() || "U"}
// //               </div>
// //             </div>
// //           </div>
// //           <div className="text-center">
// //             <h2 className="text-4xl font-black text-white tracking-tight mb-2">Connecting...</h2>
// //             <p className="text-white/40 text-xs font-black uppercase tracking-widest italic">{targetUserName || "Secure User"}</p>
// //           </div>
// //           <button onClick={endCall} className="w-16 h-16 bg-red-600 hover:bg-red-700 hover:rotate-90 rounded-2xl flex items-center justify-center text-white transition-all duration-500 shadow-2xl shadow-red-600/30">
// //             <PhoneOff className="w-7 h-7" />
// //           </button>
// //         </div>
// //       )}

// //       {/* 🚀 --- ACTIVE CALL SCREEN --- */}
// //       {callActive && (
// //         <div className={`transition-all duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] z-[1000001] ${isMinimized ? 'fixed bottom-8 right-8 w-80 h-48 pointer-events-auto rounded-[2rem] overflow-hidden shadow-2xl border border-white/20' : 'w-full h-full max-w-7xl mx-auto flex flex-col p-6'}`}>
// //           <div className="flex-1 relative bg-[#08090a] rounded-[3rem] overflow-hidden shadow-[0_64px_128px_-20px_rgba(0,0,0,0.9)] border border-white/10">
// //             <div className="w-full h-full flex flex-col items-center justify-center overflow-hidden">
// //               {isVideo ? (
// //                 <video ref={remoteVideoRef} className="w-full h-full object-cover scale-[1.02]" autoPlay playsInline controls={false} />
// //               ) : (
// //                 <div className="flex flex-col items-center gap-10">
// //                   <div className="relative">
// //                     <div className="absolute inset-0 bg-indigo-500 rounded-full blur-[80px] opacity-20 animate-pulse"></div>
// //                     <div className="w-48 h-48 bg-gradient-to-tr from-[#1e1b4b] via-[#312e81] to-[#3730a3] rounded-full flex items-center justify-center text-8xl text-white font-black shadow-2xl border-4 border-white/10">
// //                       {(callerName || targetUserName)?.charAt(0) || "U"}
// //                     </div>
// //                     <div className="absolute -bottom-2 right-1/2 translate-x-1/2 px-6 py-2 bg-indigo-600 rounded-2xl text-[10px] text-white font-black uppercase tracking-[0.3em] shadow-2xl border-4 border-[#08090a]">Secured</div>
// //                   </div>
// //                   <div className="text-center">
// //                     <h3 className="text-5xl font-black text-white mb-2 tracking-tighter">{callerName || targetUserName || "User"}</h3>
// //                     <p className="text-indigo-400 font-black uppercase tracking-[0.3em] text-[10px] animate-pulse">End-to-End Encrypted</p>
// //                   </div>
// //                   {/* <audio ref={remoteVideoRef} autoPlay /> */}
// //                   <audio ref={remoteAudioRef} autoPlay />
// //                 </div>
// //               )}
// //             </div>
// //             {isVideo && !isMinimized && (
// //               <div className="absolute top-10 right-10 w-48 h-64 bg-black/40 backdrop-blur-3xl rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/20 group cursor-move hover:border-indigo-500/50 transition-colors">
              
// //                 <video ref={localVideoRef} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" autoPlay playsInline muted />
// //                 <div className="absolute top-4 left-4 bg-black/40 px-2 py-0.5 rounded-lg text-[8px] text-white/60 font-black uppercase tracking-widest">You</div>
// //               </div>
// //             )}
// //             {!isMinimized ? (
// //               <button onClick={() => setIsMinimized(true)} className="absolute top-10 left-10 p-4 bg-white/5 hover:bg-white/10 text-white rounded-2xl transition-all border border-white/10 group">
// //                 <Minimize2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
// //               </button>
// //             ) : (
// //               <button onClick={() => setIsMinimized(false)} className="absolute inset-0 w-full h-full flex items-center justify-center bg-black/60 opacity-0 hover:opacity-100 transition-opacity z-10">
// //                 <Maximize2 className="text-white w-12 h-12" />
// //               </button>
// //             )}
// //           </div>
// //           {!isMinimized && (
// //             <div className="h-36 flex items-center justify-center gap-12 relative mt-4">
// //               <div className="bg-[#12141c]/60 backdrop-blur-[48px] px-16 py-7 rounded-[3rem] border border-white/10 flex items-center gap-12 shadow-[0_24px_48px_rgba(0,0,0,0.6)]">
// //                 <button onClick={toggleMute} className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${isMuted ? 'bg-red-500 text-white scale-110' : 'bg-white/5 text-white hover:bg-white/15'}`}>
// //                   {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
// //                 </button>
// //                 <button onClick={endCall} className="w-20 h-20 bg-red-600 hover:bg-red-700 hover:rotate-90 rounded-[2rem] flex items-center justify-center text-white transition-all duration-500 shadow-2xl shadow-red-600/40">
// //                   <PhoneOff className="w-9 h-9" />
// //                 </button>
// //                 {isVideo && (
// //                   <button onClick={toggleCamera} className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${isCameraOff ? 'bg-red-500 text-white scale-110' : 'bg-white/5 text-white hover:bg-white/15'}`}>
// //                     {isCameraOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
// //                   </button>
// //                 )}
// //               </div>
// //               <div className="absolute right-10 flex items-center gap-4 px-8 py-4 bg-white/5 rounded-3xl border border-white/10 backdrop-blur-md">
// //                 <div className="flex flex-col items-end">
// //                   <span className="text-[10px] text-white/30 uppercase font-black tracking-widest leading-none mb-1">Channel</span>
// //                   <span className="text-xs text-emerald-400 font-black tracking-tight">Active_P2P</span>
// //                 </div>
// //                 <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_12px_rgba(16,185,129,0.8)]"></div>
// //               </div>
// //             </div>
// //           )}
// //         </div>
// //       )}
// //       <style>{`
// //         @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
// //         .animate-spin-slow { animation: spin-slow 20s linear infinite; }
// //       `}</style>
// //     </div>
// //   );
// // }










// // import { useEffect, useRef, useState, useCallback } from "react";
// // import { useCallStore } from "../app/callStore";
// // import { getSocket } from "../socket/socket";
// // import { Phone, Video, Mic, MicOff, VideoOff, PhoneOff, Minimize2, Maximize2 } from "lucide-react";
// // import ringtoneSound from "../assets/VibrateCall.mp3";

// // export default function CallManager() {
// //   const {
// //     isReceivingCall,
// //     isCalling,
// //     callActive,
// //     callerName,
// //     callerSignal,
// //     callerId,
// //     targetUserId,
// //     targetUserName,
// //     isVideo,
// //     setCallState,
// //     clearCallState,
// //   } = useCallStore();


// //   const remoteAudioRef = useRef(null);
// //   const [localStream, setLocalStream] = useState(null);
// //   const [remoteStream, setRemoteStream] = useState(null);

// //   const [isMuted, setIsMuted] = useState(false);
// //   const [isCameraOff, setIsCameraOff] = useState(false);
// //   const [isMinimized, setIsMinimized] = useState(false);
// //   const [autoplayBlocked, setAutoplayBlocked] = useState(false);

// //   const localVideoRef = useRef(null);
// //   const remoteVideoRef = useRef(null);
// //   const connectionRef = useRef(null);
// //   const ringtoneRef = useRef(null);
// //   const vibrateInterval = useRef(null);
// //   const callInitiatedRef = useRef(false);

// //   // ==========================================
// //   // 🎵 HELPERS (useCallback to avoid hoisting errors)
// //   // ==========================================

// //   const startRinging = useCallback(() => {
// //     if (!ringtoneRef.current) {
// //       ringtoneRef.current = new Audio(ringtoneSound);
// //       ringtoneRef.current.loop = true;
// //     }
// //     ringtoneRef.current.play().then(() => {
// //       setAutoplayBlocked(false);
// //     }).catch(e => {
// //       console.log("Audio block:", e);
// //       setAutoplayBlocked(true);
// //     });
// //     if (navigator.vibrate) {
// //       vibrateInterval.current = setInterval(() => {
// //         navigator.vibrate([400, 200, 400]);
// //       }, 1200);
// //     }
// //   }, []);

// //   const stopRinging = useCallback(() => {
// //     if (ringtoneRef.current) {
// //       ringtoneRef.current.pause();
// //       ringtoneRef.current.currentTime = 0;
// //     }
// //     if (vibrateInterval.current) clearInterval(vibrateInterval.current);
// //     if (navigator.vibrate) navigator.vibrate(0);
// //   }, []);

// //   // const initStream = useCallback(async (videoRequired) => {
// //   //   try {
// //   //     const stream = await navigator.mediaDevices.getUserMedia({
// //   //       video: videoRequired ? { width: 1280, height: 720, facingMode: "user" } : false,
// //   //       audio: true
// //   //     });
// //   //     setLocalStream(stream);
// //   //     return stream;
// //   //   } catch (err) {
// //   //     console.error("Stream access denied:", err);
// //   //     return null;
// //   //   }
// //   // }, []);

// //   const initStream = useCallback(async () => {
// //     try {
// //       const stream = await navigator.mediaDevices.getUserMedia({
// //         video: true,
// //         audio: true
// //       });
  
// //       console.log("🎥 Tracks:", stream.getTracks());
  
// //       setLocalStream(stream);
  
// //       if (localVideoRef.current) {
// //         localVideoRef.current.srcObject = stream;
// //         localVideoRef.current.play().catch(() => {});
// //       }
  
// //       return stream;
// //     } catch (err) {
// //       console.error("Stream access denied:", err);
// //       return null;
// //     }
// //   }, []);


// //   const createPeer = useCallback((stream, socket, toUserId) => {
// //     const peer = new RTCPeerConnection({
// //       iceServers: [{ urls: "stun:stun.l.google.com:19302" }]
// //     });
  
// //     // ✅ Add tracks
// //     stream.getTracks().forEach(track => {
// //       peer.addTrack(track, stream);
// //     });
  
// //     // ✅ FIX: handle remote stream properly
// //     peer.ontrack = (event) => {
// //       console.log("🎬 Remote track:", event.streams);
  
// //       if (event.streams && event.streams[0]) {
// //         setRemoteStream(event.streams[0]);
// //       }
// //     };
  
// //     // ✅ ICE
// //     peer.onicecandidate = (event) => {
// //       if (event.candidate) {
// //         socket.emit("ice_candidate", {
// //           to: toUserId,
// //           candidate: event.candidate
// //         });
// //       }
// //     };
  
// //     return peer;
// //   }, []);




// //   const endCallCleanup = useCallback(() => {
// //     // 📝 LOG CALL IN CHAT BEFORE CLEANUP
// //     if (callActive || isCalling) {
// //       const socket = getSocket();
// //       const targetId = callerId || targetUserId;
// //       const groupId = useCallStore.getState().groupId;
// //       if (socket && (targetId || groupId)) {
// //         socket.emit("send_message", {
// //           receiverId: targetId || null,
// //           groupId: groupId || null,
// //           message: `📞 ${isVideo ? 'Video' : 'Audio'} call ended`,
// //           type: "call_log", // Custom type for UI styling
// //           clientTime: new Date(),
// //         });
// //       }
// //     }

// //     // if (localStream) localStream.getTracks().forEach(track => track.stop());
// //     if (localStream) {
// //       localStream.getTracks().forEach(track => track.stop());
// //     }
    
// //     if (remoteStream) {
// //       remoteStream.getTracks().forEach(track => track.stop());
// //     }
// //     if (connectionRef.current) {
// //       connectionRef.current.close();
// //       connectionRef.current = null;
// //     }
// //     setLocalStream(null);
// //     setRemoteStream(null);
// //     clearCallState();
// //     stopRinging();
// //     callInitiatedRef.current = false;
// //   }, [callActive, isCalling, callerId, targetUserId, isVideo, localStream, clearCallState, stopRinging]);

// //   const initiateCallFlow = useCallback(async () => {
// //     const socket = getSocket();
// //     if (!socket) return;
// //     try {
// //       const stream = await initStream(isVideo);
// //       if (!stream) {
// //         clearCallState();
// //         return;
// //       }
// //       const peer = createPeer(stream, socket, targetUserId);
// //       connectionRef.current = peer;
// //       const offer = await peer.createOffer();
// //       await peer.setLocalDescription(offer);
// //       const user = JSON.parse(localStorage.getItem("user") || "{}");
// //       socket.emit("call_user", {
// //         userToCall: targetUserId || null,
// //         groupToCall: useCallStore.getState().groupId || null,
// //         signalData: offer,
// //         from: user.id,
// //         name: `${user.first_name} ${user.last_name || ""}`,
// //         isVideo: isVideo,
// //       });
// //     } catch (err) {
// //       console.error("Initiate error:", err);
// //       endCallCleanup();
// //     }
// //   }, [isVideo, targetUserId, clearCallState, initStream, createPeer, endCallCleanup]);

// //   // ==========================================
// //   // 🧨 EFFECTS
// //   // ==========================================

// //   useEffect(() => {
// //     const socket = getSocket();
// //     if (!socket) return;

// //     const handleIncomingCall = (data) => {
// //       console.log("☎️ Incoming call from:", data.from);
// //       setCallState({
// //         isReceivingCall: true,
// //         callerId: data.from,
// //         callerName: data.groupId ? `Group: ${data.name}` : data.name,
// //         callerSignal: data.signal,
// //         isVideo: data.isVideo,
// //         groupId: data.groupId || null,
// //       });
// //       startRinging();
// //     };

// //     const handleCallAccepted = async (signal) => {
// //       console.log("✅ Call accepted! Establishing connection...");
// //       stopRinging();
// //       setCallState({ callActive: true, isCalling: false });
// //       if (connectionRef.current) {
// //         try {
// //           await connectionRef.current.setRemoteDescription(new RTCSessionDescription(signal));
// //         } catch (e) {
// //           console.error("Error setting remote description:", e);
// //         }
// //       }
// //     };

// //     const handleCallRejected = () => {
// //       stopRinging();
// //       endCallCleanup();
// //     };

// //     const handleCallEnded = () => {
// //       stopRinging();
// //       endCallCleanup();
// //     };

// //     const handleIceCandidate = async (candidate) => {
// //       if (connectionRef.current && candidate) {
// //         try {
// //           await connectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
// //         } catch (e) {
// //           console.error("Error adding ice candidate:", e);
// //         }
// //       }
// //     };

// //     socket.on("incoming_call", handleIncomingCall);
// //     socket.on("call_accepted", handleCallAccepted);
// //     socket.on("call_rejected", handleCallRejected);
// //     socket.on("call_ended", handleCallEnded);
// //     socket.on("ice_candidate", handleIceCandidate);

// //     return () => {
// //       socket.off("incoming_call", handleIncomingCall);
// //       socket.off("call_accepted", handleCallAccepted);
// //       socket.off("call_rejected", handleCallRejected);
// //       socket.off("call_ended", handleCallEnded);
// //       socket.off("ice_candidate", handleIceCandidate);
// //     };
// //   }, [setCallState, stopRinging, startRinging, endCallCleanup]);

// //   useEffect(() => {
// //     if (isCalling && !callActive && !callInitiatedRef.current) {
// //       callInitiatedRef.current = true;
// //       initiateCallFlow();
// //     } else if (!isCalling && !callActive) {
// //       callInitiatedRef.current = false;
// //     }
// //   }, [isCalling, callActive, initiateCallFlow]);



 
// //   // useEffect(() => {
// //   //   if (localVideoRef.current && localStream) {
// //   //     localVideoRef.current.srcObject = localStream;
  
// //   //     const playPromise = localVideoRef.current.play();
// //   //     if (playPromise !== undefined) {
// //   //       playPromise.catch((err) => {
// //   //         console.log("❌ Local video autoplay blocked:", err);
// //   //       });
// //   //     }
// //   //   }
// //   // }, [localStream]);

// //   useEffect(() => {
// //     if (localVideoRef.current && localStream) {
// //       console.log("✅ Attaching LOCAL stream");
  
// //       localVideoRef.current.srcObject = localStream;
// //       localVideoRef.current.muted = true; // 🔥 important
// //       localVideoRef.current.playsInline = true;
  
// //       const playPromise = localVideoRef.current.play();
// //       if (playPromise !== undefined) {
// //         playPromise.catch((err) => {
// //           console.log("❌ Local video autoplay blocked:", err);
// //         });
// //       }
// //     }
// //   }, [localStream]);


// //   useEffect(() => {
// //     if (!remoteStream) return;
  
// //     console.log("🔊 Attaching remote stream...");
  
// //     // 🎥 VIDEO
// //     if (remoteVideoRef.current) {
// //       remoteVideoRef.current.srcObject = remoteStream;
// //       remoteVideoRef.current.play().catch(() => {});
// //     }
  
// //     // 🔊 AUDIO (CRITICAL FIX)
// //     if (remoteAudioRef.current) {
// //       remoteAudioRef.current.srcObject = remoteStream;
// //       remoteAudioRef.current.play().catch(() => {});
// //     }
  
// //   }, [remoteStream, callActive]);



// //   // ==========================================
// //   // 🎮 ACTIONS
// //   // ==========================================

// //   const answerCall = async () => {
// //     stopRinging();
// //     const stream = await initStream(isVideo);
// //     if (!stream) return;
// //     setCallState({ callActive: true, isReceivingCall: false });
// //     const socket = getSocket();
// //     const peer = createPeer(stream, socket, callerId);
// //     connectionRef.current = peer;
// //     try {
// //       await peer.setRemoteDescription(new RTCSessionDescription(callerSignal));
// //       const answer = await peer.createAnswer();
// //       await peer.setLocalDescription(answer);
// //       socket.emit("make_answer", { to: callerId, signal: answer });
// //     } catch (e) {
// //       console.error("Answer error:", e);
// //       endCallCleanup();
// //     }
// //   };

// //   const rejectCall = () => {
// //     stopRinging();
// //     const socket = getSocket();
// //     if (socket) socket.emit("reject_call", { to: callerId });
// //     clearCallState();
// //   };

// //   const endCall = () => {
// //     const socket = getSocket();
// //     const targetId = callerId || targetUserId;
// //     const groupId = useCallStore.getState().groupId;

// //     if (socket) {
// //       if (targetId) socket.emit("end_call", { to: targetId });
// //       else if (groupId) socket.emit("end_call", { groupId });
// //     }
// //     endCallCleanup();
// //   };

// //   const toggleMute = () => {
// //     if (localStream) {
// //       localStream.getAudioTracks().forEach(track => {
// //         track.enabled = !track.enabled;

// //         console.log("VIDEO TRACK ENABLED:", track.enabled);
// //         setIsMuted(!track.enabled);
// //       });
// //     }
// //   };

// //   const toggleCamera = () => {
// //     if (localStream) {
// //       console.log("LOCAL STREAM TRACKS:", localStream.getVideoTracks());

// //       localStream.getVideoTracks().forEach(track => {
// //         track.enabled = !track.enabled;
// //         setIsCameraOff(!track.enabled);
// //       });
// //     }
// //   };

// //   if (!isCalling && !isReceivingCall && !callActive) return null;

// //   return (
// //     <div className={`fixed inset-0 z-[2000000] transition-all duration-700 flex items-center justify-center ${isMinimized ? 'pointer-events-none' : 'bg-[#050508]/70 backdrop-blur-[32px]'}`}>

// //       {/* 🔮 --- INCOMING CALL MODAL --- */}
// //       {isReceivingCall && !callActive && (
// //         <div className="bg-[#12141c]/90 border border-white/10 rounded-[3rem] p-12 flex flex-col items-center gap-8 shadow-[0_32px_128px_-16px_rgba(0,0,0,0.9)] w-[400px] animate-in zoom-in-95 duration-500">
// //           <div className="relative">
// //             <div className="absolute inset-0 bg-indigo-500 rounded-full blur-[50px] opacity-30 animate-pulse"></div>
// //             <div className="w-32 h-32 rounded-full p-1 bg-gradient-to-tr from-indigo-600 via-purple-500 to-pink-500 animate-spin-slow">
// //               <div className="w-full h-full bg-[#12141c] rounded-full flex justify-center items-center text-6xl text-white font-black border-2 border-[#12141c]">
// //                 {callerName?.charAt(0).toUpperCase() || "U"}
// //               </div>
// //             </div>
// //             <div className="absolute -bottom-1 -right-1 w-10 h-10 bg-emerald-500 rounded-full border-4 border-[#12141c] flex items-center justify-center animate-bounce shadow-xl shadow-emerald-500/20">
// //               <Phone className="text-white w-5 h-5 fill-white" />
// //             </div>
// //           </div>
// //           <div className="text-center">
// //             <h2 className="text-4xl font-black text-white tracking-tight mb-3">{callerName || "Unknown"}</h2>
// //             {autoplayBlocked && (
// //               <button
// //                 onClick={() => {
// //                   ringtoneRef.current?.play();
// //                   setAutoplayBlocked(false);
// //                 }}
// //                 className="mb-4 text-[10px] text-indigo-400 font-bold uppercase animate-pulse underline"
// //               >
// //                 Click to enable ringtone
// //               </button>
// //             )}
// //             <p className="text-purple-400 font-black uppercase tracking-[0.2em] text-[10px] bg-purple-500/10 px-4 py-1.5 rounded-full border border-purple-500/20">
// //               {isVideo ? "Incoming Video Request" : "Incoming Audio Request"}
// //             </p>
// //           </div>
// //           <div className="flex gap-12 mt-2">
// //             <button onClick={rejectCall} className="group flex flex-col items-center gap-3">
// //               <div className="w-16 h-16 bg-red-500/10 hover:bg-red-500 rounded-2xl flex items-center justify-center text-red-500 hover:text-white transition-all duration-300 border border-red-500/20 shadow-lg shadow-red-500/10">
// //                 <PhoneOff className="w-7 h-7" />
// //               </div>
// //               <span className="text-[10px] text-white/40 uppercase font-black tracking-widest">Decline</span>
// //             </button>
// //             <button onClick={answerCall} className="group flex flex-col items-center gap-3">
// //               <div className="w-16 h-16 bg-emerald-500/10 hover:bg-emerald-500 rounded-2xl flex items-center justify-center text-emerald-500 hover:text-white transition-all duration-300 border border-emerald-500/20 shadow-lg shadow-emerald-500/10">
// //                 {isVideo ? <Video className="w-7 h-7" /> : <Phone className="w-7 h-7" />}
// //               </div>
// //               <span className="text-[10px] text-white/40 uppercase font-black tracking-widest">Accept</span>
// //             </button>
// //           </div>
// //         </div>
// //       )}

// //       {/* 📡 --- OUTGOING CALL MODAL --- */}
// //       {isCalling && !callActive && (
// //         <div className="bg-[#12141c]/90 border border-white/10 rounded-[3rem] p-12 flex flex-col items-center gap-8 shadow-[0_32px_128px_-16px_rgba(0,0,0,0.9)] w-[400px] animate-in slide-in-from-bottom-20 duration-500">
// //           <div className="relative">
// //             <div className="absolute inset-0 bg-blue-500 rounded-full blur-[50px] opacity-30 animate-pulse"></div>
// //             <div className="w-32 h-32 rounded-full border-2 border-dashed border-blue-500/40 flex justify-center items-center animate-spin-slow">
// //               <div className="w-24 h-24 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-full flex justify-center items-center text-5xl text-white font-black shadow-2xl">
// //                 {targetUserName?.charAt(0).toUpperCase() || "U"}
// //               </div>
// //             </div>
// //           </div>
// //           <div className="text-center">
// //             <h2 className="text-4xl font-black text-white tracking-tight mb-2">Connecting...</h2>
// //             <p className="text-white/40 text-xs font-black uppercase tracking-widest italic">{targetUserName || "Secure User"}</p>
// //           </div>
// //           <button onClick={endCall} className="w-16 h-16 bg-red-600 hover:bg-red-700 hover:rotate-90 rounded-2xl flex items-center justify-center text-white transition-all duration-500 shadow-2xl shadow-red-600/30">
// //             <PhoneOff className="w-7 h-7" />
// //           </button>
// //         </div>
// //       )}

// //       {/* 🚀 --- ACTIVE CALL SCREEN --- */}
// //       {callActive && (
// //         <div className={`transition-all duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] z-[1000001] ${isMinimized ? 'fixed bottom-8 right-8 w-80 h-48 pointer-events-auto rounded-[2rem] overflow-hidden shadow-2xl border border-white/20' : 'w-full h-full max-w-7xl mx-auto flex flex-col p-6'}`}>
// //           <div className="flex-1 relative bg-[#08090a] rounded-[3rem] overflow-hidden shadow-[0_64px_128px_-20px_rgba(0,0,0,0.9)] border border-white/10">
// //             <div className="w-full h-full flex flex-col items-center justify-center overflow-hidden">
// //               {isVideo ? (
// //                 <video ref={remoteVideoRef} className="w-full h-full object-cover scale-[1.02]" autoPlay playsInline controls={false} />
// //               ) : (
// //                 <div className="flex flex-col items-center gap-10">
// //                   <div className="relative">
// //                     <div className="absolute inset-0 bg-indigo-500 rounded-full blur-[80px] opacity-20 animate-pulse"></div>
// //                     <div className="w-48 h-48 bg-gradient-to-tr from-[#1e1b4b] via-[#312e81] to-[#3730a3] rounded-full flex items-center justify-center text-8xl text-white font-black shadow-2xl border-4 border-white/10">
// //                       {(callerName || targetUserName)?.charAt(0) || "U"}
// //                     </div>
// //                     <div className="absolute -bottom-2 right-1/2 translate-x-1/2 px-6 py-2 bg-indigo-600 rounded-2xl text-[10px] text-white font-black uppercase tracking-[0.3em] shadow-2xl border-4 border-[#08090a]">Secured</div>
// //                   </div>
// //                   <div className="text-center">
// //                     <h3 className="text-5xl font-black text-white mb-2 tracking-tighter">{callerName || targetUserName || "User"}</h3>
// //                     <p className="text-indigo-400 font-black uppercase tracking-[0.3em] text-[10px] animate-pulse">End-to-End Encrypted</p>
// //                   </div>
// //                   {/* <audio ref={remoteVideoRef} autoPlay /> */}
// //                   <audio ref={remoteAudioRef} autoPlay />
// //                 </div>
// //               )}
// //             </div>
// //             {isVideo && !isMinimized && (
// //               <div className="absolute top-10 right-10 w-48 h-64 bg-black/40 backdrop-blur-3xl rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/20 group cursor-move hover:border-indigo-500/50 transition-colors">
              
// // {/* <div className="absolute top-10 right-10 w-48 h-64 overflow-hidden rounded-[2.5rem] border border-white/20">                <video ref={localVideoRef} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" autoPlay playsInline muted /> */}
// //                 <div className="absolute top-4 left-4 bg-black/40 px-2 py-0.5 rounded-lg text-[8px] text-white/60 font-black uppercase tracking-widest">You</div>
// //               </div>
// //             )}
// //             {!isMinimized ? (
// //               <button onClick={() => setIsMinimized(true)} className="absolute top-10 left-10 p-4 bg-white/5 hover:bg-white/10 text-white rounded-2xl transition-all border border-white/10 group">
// //                 <Minimize2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
// //               </button>
// //             ) : (
// //               <button onClick={() => setIsMinimized(false)} className="absolute inset-0 w-full h-full flex items-center justify-center bg-black/60 opacity-0 hover:opacity-100 transition-opacity z-10">
// //                 <Maximize2 className="text-white w-12 h-12" />
// //               </button>
// //             )}
// //           </div>
// //           {!isMinimized && (
// //             <div className="h-36 flex items-center justify-center gap-12 relative mt-4">
// //               <div className="bg-[#12141c]/60 backdrop-blur-[48px] px-16 py-7 rounded-[3rem] border border-white/10 flex items-center gap-12 shadow-[0_24px_48px_rgba(0,0,0,0.6)]">
// //                 <button onClick={toggleMute} className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${isMuted ? 'bg-red-500 text-white scale-110' : 'bg-white/5 text-white hover:bg-white/15'}`}>
// //                   {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
// //                 </button>
// //                 <button onClick={endCall} className="w-20 h-20 bg-red-600 hover:bg-red-700 hover:rotate-90 rounded-[2rem] flex items-center justify-center text-white transition-all duration-500 shadow-2xl shadow-red-600/40">
// //                   <PhoneOff className="w-9 h-9" />
// //                 </button>
// //                 {isVideo && (
// //                   <button onClick={toggleCamera} className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${isCameraOff ? 'bg-red-500 text-white scale-110' : 'bg-white/5 text-white hover:bg-white/15'}`}>
// //                     {isCameraOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
// //                   </button>
// //                 )}
// //               </div>
// //               <div className="absolute right-10 flex items-center gap-4 px-8 py-4 bg-white/5 rounded-3xl border border-white/10 backdrop-blur-md">
// //                 <div className="flex flex-col items-end">
// //                   <span className="text-[10px] text-white/30 uppercase font-black tracking-widest leading-none mb-1">Channel</span>
// //                   <span className="text-xs text-emerald-400 font-black tracking-tight">Active_P2P</span>
// //                 </div>
// //                 <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_12px_rgba(16,185,129,0.8)]"></div>
// //               </div>
// //             </div>
// //           )}
// //         </div>
// //       )}
// //       <style>{`
// //         @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
// //         .animate-spin-slow { animation: spin-slow 20s linear infinite; }
// //       `}</style>
// //     </div>
// //   );
// // }





// // import { useEffect, useRef, useState, useCallback } from "react";
// // import { useCallStore } from "../app/callStore";
// // import { getSocket } from "../socket/socket";
// // import { Phone, Video, Mic, MicOff, VideoOff, PhoneOff, Minimize2, Maximize2 } from "lucide-react";
// // import ringtoneSound from "../assets/VibrateCall.mp3";

// // export default function CallManager() {
// //   const {
// //     isReceivingCall,
// //     isCalling,
// //     callActive,
// //     callerName,
// //     callerSignal,
// //     callerId,
// //     targetUserId,
// //     targetUserName,
// //     isVideo,
// //     setCallState,
// //     clearCallState,
// //   } = useCallStore();

// //   const [localStream, setLocalStream] = useState(null);
// //   const [remoteStream, setRemoteStream] = useState(null);

// //   const [isMuted, setIsMuted] = useState(false);
// //   const [isCameraOff, setIsCameraOff] = useState(false);
// //   const [isMinimized, setIsMinimized] = useState(false);
// //   const [autoplayBlocked, setAutoplayBlocked] = useState(false);

// //   const localVideoRef = useRef(null);
// //   const remoteVideoRef = useRef(null);
// //   const connectionRef = useRef(null);
// //   const ringtoneRef = useRef(null);
// //   const vibrateInterval = useRef(null);
// //   const callInitiatedRef = useRef(false);

// //   // ==========================================
// //   // 🎵 HELPERS (useCallback to avoid hoisting errors)
// //   // ==========================================

// //   const startRinging = useCallback(() => {
// //     if (!ringtoneRef.current) {
// //       ringtoneRef.current = new Audio(ringtoneSound);
// //       ringtoneRef.current.loop = true;
// //     }
// //     ringtoneRef.current.play().then(() => {
// //       setAutoplayBlocked(false);
// //     }).catch(e => {
// //       console.log("Audio block:", e);
// //       setAutoplayBlocked(true);
// //     });
// //     if (navigator.vibrate) {
// //       vibrateInterval.current = setInterval(() => {
// //         navigator.vibrate([400, 200, 400]);
// //       }, 1200);
// //     }
// //   }, []);

// //   const stopRinging = useCallback(() => {
// //     if (ringtoneRef.current) {
// //       ringtoneRef.current.pause();
// //       ringtoneRef.current.currentTime = 0;
// //     }
// //     if (vibrateInterval.current) clearInterval(vibrateInterval.current);
// //     if (navigator.vibrate) navigator.vibrate(0);
// //   }, []);

// //   const initStream = useCallback(async (videoRequired) => {
// //     try {
// //       const stream = await navigator.mediaDevices.getUserMedia({
// //         video: videoRequired ? { width: 1280, height: 720, facingMode: "user" } : false,
// //         audio: true
// //       });
// //       setLocalStream(stream);
// //       return stream;
// //     } catch (err) {
// //       console.error("Stream access denied:", err);
// //       return null;
// //     }
// //   }, []);

// //   const createPeer = useCallback((stream, socket, toUserId) => {
// //     const peer = new RTCPeerConnection({
// //       iceServers: [{ urls: "stun:stun.l.google.com:19302" }]
// //     });
// //     stream.getTracks().forEach((track) => peer.addTrack(track, stream));
// //     peer.ontrack = (event) => {
// //       console.log("🎬 Remote track received:", event.streams[0]);
// //       setRemoteStream(event.streams[0]);
// //     };
// //     peer.onicecandidate = (event) => {
// //       if (event.candidate) {
// //         socket.emit("ice_candidate", { to: toUserId, candidate: event.candidate });
// //       }
// //     };
// //     return peer;
// //   }, []);

// //   const endCallCleanup = useCallback(() => {
// //     // 📝 LOG CALL IN CHAT BEFORE CLEANUP
// //     if (callActive || isCalling) {
// //       const socket = getSocket();
// //       const targetId = callerId || targetUserId;
// //       const groupId = useCallStore.getState().groupId;
// //       if (socket && (targetId || groupId)) {
// //         socket.emit("send_message", {
// //           receiverId: targetId || null,
// //           groupId: groupId || null,
// //           message: `📞 ${isVideo ? 'Video' : 'Audio'} call ended`,
// //           type: "call_log", // Custom type for UI styling
// //           clientTime: new Date(),
// //         });
// //       }
// //     }

// //     if (localStream) localStream.getTracks().forEach(track => track.stop());
// //     if (connectionRef.current) {
// //       connectionRef.current.close();
// //       connectionRef.current = null;
// //     }
// //     setLocalStream(null);
// //     setRemoteStream(null);
// //     clearCallState();
// //     stopRinging();
// //     callInitiatedRef.current = false;
// //   }, [callActive, isCalling, callerId, targetUserId, isVideo, localStream, clearCallState, stopRinging]);

// //   const initiateCallFlow = useCallback(async () => {
// //     const socket = getSocket();
// //     if (!socket) return;
// //     try {
// //       const stream = await initStream(isVideo);
// //       if (!stream) {
// //         clearCallState();
// //         return;
// //       }
// //       const peer = createPeer(stream, socket, targetUserId);
// //       connectionRef.current = peer;
// //       const offer = await peer.createOffer();
// //       await peer.setLocalDescription(offer);
// //       const user = JSON.parse(localStorage.getItem("user") || "{}");
// //       socket.emit("call_user", {
// //         userToCall: targetUserId || null,
// //         groupToCall: useCallStore.getState().groupId || null,
// //         signalData: offer,
// //         from: user.id,
// //         name: `${user.first_name} ${user.last_name || ""}`,
// //         isVideo: isVideo,
// //       });
// //     } catch (err) {
// //       console.error("Initiate error:", err);
// //       endCallCleanup();
// //     }
// //   }, [isVideo, targetUserId, clearCallState, initStream, createPeer, endCallCleanup]);

// //   // ==========================================
// //   // 🧨 EFFECTS
// //   // ==========================================

// //   useEffect(() => {
// //     const socket = getSocket();
// //     if (!socket) return;

// //     const handleIncomingCall = (data) => {
// //       console.log("☎️ Incoming call from:", data.from);
// //       setCallState({
// //         isReceivingCall: true,
// //         callerId: data.from,
// //         callerName: data.groupId ? `Group: ${data.name}` : data.name,
// //         callerSignal: data.signal,
// //         isVideo: data.isVideo,
// //         groupId: data.groupId || null,
// //       });
// //       startRinging();
// //     };

// //     const handleCallAccepted = async (signal) => {
// //       console.log("✅ Call accepted! Establishing connection...");
// //       stopRinging();
// //       setCallState({ callActive: true, isCalling: false });
// //       if (connectionRef.current) {
// //         try {
// //           await connectionRef.current.setRemoteDescription(new RTCSessionDescription(signal));
// //         } catch (e) {
// //           console.error("Error setting remote description:", e);
// //         }
// //       }
// //     };

// //     const handleCallRejected = () => {
// //       stopRinging();
// //       endCallCleanup();
// //     };

// //     const handleCallEnded = () => {
// //       stopRinging();
// //       endCallCleanup();
// //     };

// //     const handleIceCandidate = async (candidate) => {
// //       if (connectionRef.current && candidate) {
// //         try {
// //           await connectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
// //         } catch (e) {
// //           console.error("Error adding ice candidate:", e);
// //         }
// //       }
// //     };

// //     socket.on("incoming_call", handleIncomingCall);
// //     socket.on("call_accepted", handleCallAccepted);
// //     socket.on("call_rejected", handleCallRejected);
// //     socket.on("call_ended", handleCallEnded);
// //     socket.on("ice_candidate", handleIceCandidate);

// //     return () => {
// //       socket.off("incoming_call", handleIncomingCall);
// //       socket.off("call_accepted", handleCallAccepted);
// //       socket.off("call_rejected", handleCallRejected);
// //       socket.off("call_ended", handleCallEnded);
// //       socket.off("ice_candidate", handleIceCandidate);
// //     };
// //   }, [setCallState, stopRinging, startRinging, endCallCleanup]);

// //   useEffect(() => {
// //     if (isCalling && !callActive && !callInitiatedRef.current) {
// //       callInitiatedRef.current = true;
// //       initiateCallFlow();
// //     } else if (!isCalling && !callActive) {
// //       callInitiatedRef.current = false;
// //     }
// //   }, [isCalling, callActive, initiateCallFlow]);

// //   useEffect(() => {
// //     if (localVideoRef.current && localStream) {
// //       localVideoRef.current.srcObject = localStream;
// //     }
// //   }, [localStream]);

// //   useEffect(() => {
// //     if (remoteVideoRef.current && remoteStream) {
// //       console.log("🔊 Attaching remote stream to element...");
// //       remoteVideoRef.current.srcObject = remoteStream;

// //       const playPromise = remoteVideoRef.current.play();
// //       if (playPromise !== undefined) {
// //         playPromise.catch(error => {
// //           console.log("⚠️ Remote media playback blocked by browser:", error);
// //         });
// //       }
// //     }
// //   }, [remoteStream, callActive]);

// //   // ==========================================
// //   // 🎮 ACTIONS
// //   // ==========================================

// //   const answerCall = async () => {
// //     stopRinging();
// //     const stream = await initStream(isVideo);
// //     if (!stream) return;
// //     setCallState({ callActive: true, isReceivingCall: false });
// //     const socket = getSocket();
// //     const peer = createPeer(stream, socket, callerId);
// //     connectionRef.current = peer;
// //     try {
// //       await peer.setRemoteDescription(new RTCSessionDescription(callerSignal));
// //       const answer = await peer.createAnswer();
// //       await peer.setLocalDescription(answer);
// //       socket.emit("make_answer", { to: callerId, signal: answer });
// //     } catch (e) {
// //       console.error("Answer error:", e);
// //       endCallCleanup();
// //     }
// //   };

// //   const rejectCall = () => {
// //     stopRinging();
// //     const socket = getSocket();
// //     if (socket) socket.emit("reject_call", { to: callerId });
// //     clearCallState();
// //   };

// //   const endCall = () => {
// //     const socket = getSocket();
// //     const targetId = callerId || targetUserId;
// //     const groupId = useCallStore.getState().groupId;

// //     if (socket) {
// //       if (targetId) socket.emit("end_call", { to: targetId });
// //       else if (groupId) socket.emit("end_call", { groupId });
// //     }
// //     endCallCleanup();
// //   };

// //   const toggleMute = () => {
// //     if (localStream) {
// //       localStream.getAudioTracks().forEach(track => {
// //         track.enabled = !track.enabled;
// //         setIsMuted(!track.enabled);
// //       });
// //     }
// //   };

// //   const toggleCamera = () => {
// //     if (localStream) {
// //       localStream.getVideoTracks().forEach(track => {
// //         track.enabled = !track.enabled;
// //         setIsCameraOff(!track.enabled);
// //       });
// //     }
// //   };

// //   if (!isCalling && !isReceivingCall && !callActive) return null;

// //   return (
// //     <div className={`fixed inset-0 z-[2000000] transition-all duration-700 flex items-center justify-center ${isMinimized ? 'pointer-events-none' : 'bg-[#050508]/70 backdrop-blur-[32px]'}`}>

// //       {/* 🔮 --- INCOMING CALL MODAL --- */}
// //       {isReceivingCall && !callActive && (
// //         <div className="bg-[#12141c]/90 border border-white/10 rounded-[3rem] p-12 flex flex-col items-center gap-8 shadow-[0_32px_128px_-16px_rgba(0,0,0,0.9)] w-[400px] animate-in zoom-in-95 duration-500">
// //           <div className="relative">
// //             <div className="absolute inset-0 bg-indigo-500 rounded-full blur-[50px] opacity-30 animate-pulse"></div>
// //             <div className="w-32 h-32 rounded-full p-1 bg-gradient-to-tr from-indigo-600 via-purple-500 to-pink-500 animate-spin-slow">
// //               <div className="w-full h-full bg-[#12141c] rounded-full flex justify-center items-center text-6xl text-white font-black border-2 border-[#12141c]">
// //                 {callerName?.charAt(0).toUpperCase() || "U"}
// //               </div>
// //             </div>
// //             <div className="absolute -bottom-1 -right-1 w-10 h-10 bg-emerald-500 rounded-full border-4 border-[#12141c] flex items-center justify-center animate-bounce shadow-xl shadow-emerald-500/20">
// //               <Phone className="text-white w-5 h-5 fill-white" />
// //             </div>
// //           </div>
// //           <div className="text-center">
// //             <h2 className="text-4xl font-black text-white tracking-tight mb-3">{callerName || "Unknown"}</h2>
// //             {autoplayBlocked && (
// //               <button
// //                 onClick={() => {
// //                   ringtoneRef.current?.play();
// //                   setAutoplayBlocked(false);
// //                 }}
// //                 className="mb-4 text-[10px] text-indigo-400 font-bold uppercase animate-pulse underline"
// //               >
// //                 Click to enable ringtone
// //               </button>
// //             )}
// //             <p className="text-purple-400 font-black uppercase tracking-[0.2em] text-[10px] bg-purple-500/10 px-4 py-1.5 rounded-full border border-purple-500/20">
// //               {isVideo ? "Incoming Video Request" : "Incoming Audio Request"}
// //             </p>
// //           </div>
// //           <div className="flex gap-12 mt-2">
// //             <button onClick={rejectCall} className="group flex flex-col items-center gap-3">
// //               <div className="w-16 h-16 bg-red-500/10 hover:bg-red-500 rounded-2xl flex items-center justify-center text-red-500 hover:text-white transition-all duration-300 border border-red-500/20 shadow-lg shadow-red-500/10">
// //                 <PhoneOff className="w-7 h-7" />
// //               </div>
// //               <span className="text-[10px] text-white/40 uppercase font-black tracking-widest">Decline</span>
// //             </button>
// //             <button onClick={answerCall} className="group flex flex-col items-center gap-3">
// //               <div className="w-16 h-16 bg-emerald-500/10 hover:bg-emerald-500 rounded-2xl flex items-center justify-center text-emerald-500 hover:text-white transition-all duration-300 border border-emerald-500/20 shadow-lg shadow-emerald-500/10">
// //                 {isVideo ? <Video className="w-7 h-7" /> : <Phone className="w-7 h-7" />}
// //               </div>
// //               <span className="text-[10px] text-white/40 uppercase font-black tracking-widest">Accept</span>
// //             </button>
// //           </div>
// //         </div>
// //       )}

// //       {/* 📡 --- OUTGOING CALL MODAL --- */}
// //       {isCalling && !callActive && (
// //         <div className="bg-[#12141c]/90 border border-white/10 rounded-[3rem] p-12 flex flex-col items-center gap-8 shadow-[0_32px_128px_-16px_rgba(0,0,0,0.9)] w-[400px] animate-in slide-in-from-bottom-20 duration-500">
// //           <div className="relative">
// //             <div className="absolute inset-0 bg-blue-500 rounded-full blur-[50px] opacity-30 animate-pulse"></div>
// //             <div className="w-32 h-32 rounded-full border-2 border-dashed border-blue-500/40 flex justify-center items-center animate-spin-slow">
// //               <div className="w-24 h-24 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-full flex justify-center items-center text-5xl text-white font-black shadow-2xl">
// //                 {targetUserName?.charAt(0).toUpperCase() || "U"}
// //               </div>
// //             </div>
// //           </div>
// //           <div className="text-center">
// //             <h2 className="text-4xl font-black text-white tracking-tight mb-2">Connecting...</h2>
// //             <p className="text-white/40 text-xs font-black uppercase tracking-widest italic">{targetUserName || "Secure User"}</p>
// //           </div>
// //           <button onClick={endCall} className="w-16 h-16 bg-red-600 hover:bg-red-700 hover:rotate-90 rounded-2xl flex items-center justify-center text-white transition-all duration-500 shadow-2xl shadow-red-600/30">
// //             <PhoneOff className="w-7 h-7" />
// //           </button>
// //         </div>
// //       )}

// //       {/* 🚀 --- ACTIVE CALL SCREEN --- */}
// //       {callActive && (
// //         <div className={`transition-all duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] z-[1000001] ${isMinimized ? 'fixed bottom-8 right-8 w-80 h-48 pointer-events-auto rounded-[2rem] overflow-hidden shadow-2xl border border-white/20' : 'w-full h-full max-w-7xl mx-auto flex flex-col p-6'}`}>
// //           <div className="flex-1 relative bg-[#08090a] rounded-[3rem] overflow-hidden shadow-[0_64px_128px_-20px_rgba(0,0,0,0.9)] border border-white/10">
// //             <div className="w-full h-full flex flex-col items-center justify-center overflow-hidden">
// //               {isVideo ? (
// //                 <video ref={remoteVideoRef} className="w-full h-full object-cover scale-[1.02]" autoPlay playsInline />
// //               ) : (
// //                 <div className="flex flex-col items-center gap-10">
// //                   <div className="relative">
// //                     <div className="absolute inset-0 bg-indigo-500 rounded-full blur-[80px] opacity-20 animate-pulse"></div>
// //                     <div className="w-48 h-48 bg-gradient-to-tr from-[#1e1b4b] via-[#312e81] to-[#3730a3] rounded-full flex items-center justify-center text-8xl text-white font-black shadow-2xl border-4 border-white/10">
// //                       {(callerName || targetUserName)?.charAt(0) || "U"}
// //                     </div>
// //                     <div className="absolute -bottom-2 right-1/2 translate-x-1/2 px-6 py-2 bg-indigo-600 rounded-2xl text-[10px] text-white font-black uppercase tracking-[0.3em] shadow-2xl border-4 border-[#08090a]">Secured</div>
// //                   </div>
// //                   <div className="text-center">
// //                     <h3 className="text-5xl font-black text-white mb-2 tracking-tighter">{callerName || targetUserName || "User"}</h3>
// //                     <p className="text-indigo-400 font-black uppercase tracking-[0.3em] text-[10px] animate-pulse">End-to-End Encrypted</p>
// //                   </div>
// //                   <audio ref={remoteVideoRef} autoPlay />
// //                 </div>
// //               )}
// //             </div>
// //             {isVideo && !isMinimized && (
//               // <div className="absolute top-10 right-10 w-48 h-64 bg-black/40 backdrop-blur-3xl rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/20 group cursor-move hover:border-indigo-500/50 transition-colors">
// //                 <video ref={localVideoRef} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" autoPlay playsInline muted />
// //                 <div className="absolute top-4 left-4 bg-black/40 px-2 py-0.5 rounded-lg text-[8px] text-white/60 font-black uppercase tracking-widest">You</div>
// //               </div>
// //             )}
// //             {!isMinimized ? (
// //               <button onClick={() => setIsMinimized(true)} className="absolute top-10 left-10 p-4 bg-white/5 hover:bg-white/10 text-white rounded-2xl transition-all border border-white/10 group">
// //                 <Minimize2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
// //               </button>
// //             ) : (
// //               <button onClick={() => setIsMinimized(false)} className="absolute inset-0 w-full h-full flex items-center justify-center bg-black/60 opacity-0 hover:opacity-100 transition-opacity z-10">
// //                 <Maximize2 className="text-white w-12 h-12" />
// //               </button>
// //             )}
// //           </div>
// //           {!isMinimized && (
// //             <div className="h-36 flex items-center justify-center gap-12 relative mt-4">
// //               <div className="bg-[#12141c]/60 backdrop-blur-[48px] px-16 py-7 rounded-[3rem] border border-white/10 flex items-center gap-12 shadow-[0_24px_48px_rgba(0,0,0,0.6)]">
// //                 <button onClick={toggleMute} className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${isMuted ? 'bg-red-500 text-white scale-110' : 'bg-white/5 text-white hover:bg-white/15'}`}>
// //                   {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
// //                 </button>
// //                 <button onClick={endCall} className="w-20 h-20 bg-red-600 hover:bg-red-700 hover:rotate-90 rounded-[2rem] flex items-center justify-center text-white transition-all duration-500 shadow-2xl shadow-red-600/40">
// //                   <PhoneOff className="w-9 h-9" />
// //                 </button>
// //                 {isVideo && (
// //                   <button onClick={toggleCamera} className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${isCameraOff ? 'bg-red-500 text-white scale-110' : 'bg-white/5 text-white hover:bg-white/15'}`}>
// //                     {isCameraOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
// //                   </button>
// //                 )}
// //               </div>
// //               <div className="absolute right-10 flex items-center gap-4 px-8 py-4 bg-white/5 rounded-3xl border border-white/10 backdrop-blur-md">
// //                 <div className="flex flex-col items-end">
// //                   <span className="text-[10px] text-white/30 uppercase font-black tracking-widest leading-none mb-1">Channel</span>
// //                   <span className="text-xs text-emerald-400 font-black tracking-tight">Active_P2P</span>
// //                 </div>
// //                 <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_12px_rgba(16,185,129,0.8)]"></div>
// //               </div>
// //             </div>
// //           )}
// //         </div>
// //       )}
// //       <style>{`
// //         @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
// //         .animate-spin-slow { animation: spin-slow 20s linear infinite; }
// //       `}</style>
// //     </div>
// //   );
// // }
